/**
 * An object that is in the scene: where it is, what it is made of, and the two
 * things the page can still change about it.
 *
 * Held by `www/src/place.ts` for as long as the object is being placed, and by
 * `www/src/index.ts` afterwards -- an object outlives the robot that was in the
 * scene when it was dropped, so the handle has to outlive the placement panel
 * (see [`AddedObject::js_reindex`]).
 */
export class AddedObject {
    static __wrap(ptr) {
        const obj = Object.create(AddedObject.prototype);
        obj.__wbg_ptr = ptr;
        AddedObjectFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AddedObjectFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_addedobject_free(ptr, 0);
    }
    /**
     * kg/m^3.
     * @returns {number}
     */
    get density() {
        const ret = wasm.addedobject_density(this.__wbg_ptr);
        return ret;
    }
    /**
     * The body's frame: the page builds its mesh names off this, so it is what
     * has to be asked for to take them out again.
     * @returns {string}
     */
    get frame() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedobject_frame(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The coefficient of friction against the ground.
     * @returns {number}
     */
    get friction() {
        const ret = wasm.addedobject_friction(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {number}
     */
    get index() {
        const ret = wasm.addedobject_index(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * kg.
     * @returns {number}
     */
    get mass() {
        const ret = wasm.addedobject_mass(this.__wbg_ptr);
        return ret;
    }
    /**
     * The material's name, or `None` if either number was typed in directly.
     * @returns {string | undefined}
     */
    get material() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedobject_material(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedobject_name(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * What reading the mesh had to approximate, for the same panel a robot's
     * notes go to.
     * @returns {string[]}
     */
    get notes() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedobject_notes(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Follow the object to its new place in the scene.
     *
     * Dropping a robot takes every *other* robot out and renumbers what is
     * left, and the objects that survive it move down with everything else.
     * The page is what knows which handle is which, so it tells them.
     * @param {number} index
     */
    reindex(index) {
        wasm.addedobject_reindex(this.__wbg_ptr, index);
    }
    /**
     * Take the object back out of the scene, for a placement the user gave up
     * on.
     *
     * Only ever the last articulated: a drop is refused while one is being
     * placed, so nothing can have been added on top of it.
     * @param {InterfaceHybrid} hybrid
     */
    removeFrom(hybrid) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            _assertClass(hybrid, InterfaceHybrid);
            wasm.addedobject_removeFrom(retptr, this.__wbg_ptr, hybrid.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Or out of something the table has no name for.
     * @param {InterfaceHybrid} hybrid
     * @param {number} density
     */
    setDensity(hybrid, density) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            _assertClass(hybrid, InterfaceHybrid);
            wasm.addedobject_setDensity(retptr, this.__wbg_ptr, hybrid.__wbg_ptr, density);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The other half of the same answer: what it slides on the ground at.
     *
     * Zero is allowed, unlike a zero density -- a frictionless object is a
     * thing someone may well want to watch, whereas a massless one has no
     * inertia for the solver to invert.
     * @param {InterfaceHybrid} hybrid
     * @param {number} friction
     */
    setFriction(hybrid, friction) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            _assertClass(hybrid, InterfaceHybrid);
            wasm.addedobject_setFriction(retptr, this.__wbg_ptr, hybrid.__wbg_ptr, friction);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Make the object out of a material in the table.
     * @param {InterfaceHybrid} hybrid
     * @param {string} name
     */
    setMaterial(hybrid, name) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            _assertClass(hybrid, InterfaceHybrid);
            const ptr0 = passStringToWasm0(name, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.addedobject_setMaterial(retptr, this.__wbg_ptr, hybrid.__wbg_ptr, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Put the object where the page's gizmo has dragged it: a translation and a
     * quaternion, straight onto the floating joint.
     *
     * The velocity goes to zero with it. The object is placed with the physics
     * stopped, so it has none to keep -- but a body dragged around and then let
     * go should start from rest whatever it was doing when it was picked up.
     * @param {InterfaceHybrid} hybrid
     * @param {number} x
     * @param {number} y
     * @param {number} z
     * @param {number} qx
     * @param {number} qy
     * @param {number} qz
     * @param {number} qw
     */
    setPose(hybrid, x, y, z, qx, qy, qz, qw) {
        _assertClass(hybrid, InterfaceHybrid);
        wasm.addedobject_setPose(this.__wbg_ptr, hybrid.__wbg_ptr, x, y, z, qx, qy, qz, qw);
    }
    /**
     * m^3.
     * @returns {number}
     */
    get volume() {
        const ret = wasm.addedobject_volume(this.__wbg_ptr);
        return ret;
    }
}
if (Symbol.dispose) AddedObject.prototype[Symbol.dispose] = AddedObject.prototype.free;

/**
 * A robot that is in the scene: where it is, and the pose it arrived in.
 *
 * Handed to JS and kept for as long as the robot is simulated. The editor is
 * what holds it, and frees it when the next drop replaces the robot -- as the
 * page holds the plan it came from beside it, since building a robot borrows
 * the plan rather than spending it and "Edit config" builds the same plan
 * again.
 */
export class AddedRobot {
    static __wrap(ptr) {
        const obj = Object.create(AddedRobot.prototype);
        obj.__wbg_ptr = ptr;
        AddedRobotFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AddedRobotFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_addedrobot_free(ptr, 0);
    }
    /**
     * Which body of this articulated it rides on -- what
     * `pose_articulated_body` is asked for to find out where it is looking
     * from.
     * @param {number} camera
     * @returns {number | undefined}
     */
    cameraBody(camera) {
        const ret = wasm.addedrobot_cameraBody(this.__wbg_ptr, camera);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * How many cameras it has a sensor fitted to, which is how many views
     * there are to offer.
     * @returns {number}
     */
    get cameraCount() {
        const ret = wasm.addedrobot_cameraCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Its vertical field of view in degrees. The vertical one because that is
     * what a perspective projection is set from; the horizontal follows from
     * it and the shape below.
     * @param {number} camera
     * @returns {number | undefined}
     */
    cameraFovDegrees(camera) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_cameraFovDegrees(retptr, this.__wbg_ptr, camera);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r2 = getDataViewMemory0().getFloat64(retptr + 8 * 1, true);
            return r0 === 0 ? undefined : r2;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * How many frames a second it reads that array out at, which is how often
     * there is a new image to show.
     * @param {number} camera
     * @returns {number | undefined}
     */
    cameraFps(camera) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_cameraFps(retptr, this.__wbg_ptr, camera);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r2 = getDataViewMemory0().getFloat64(retptr + 8 * 1, true);
            return r0 === 0 ? undefined : r2;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The link one of them rides on, which is what names the view.
     * @param {number} camera
     * @returns {string | undefined}
     */
    cameraLink(camera) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_cameraLink(retptr, this.__wbg_ptr, camera);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The sensor fitted to it, as [`CAMERAS`] names it.
     * @param {number} camera
     * @returns {string | undefined}
     */
    cameraModel(camera) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_cameraModel(retptr, this.__wbg_ptr, camera);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The sensor's full-resolution array, `[width, height]` in pixels. Empty
     * for a camera that is not there. What a view takes from it is the shape;
     * drawing 3 megapixels of it into a corner of the page is not the point.
     * @param {number} camera
     * @returns {Uint32Array}
     */
    cameraResolution(camera) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_cameraResolution(retptr, this.__wbg_ptr, camera);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayU32FromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Which articulated this is, for everything that addresses one by index --
     * visuals, the UART, rebooting the controller.
     * @returns {number}
     */
    get index() {
        const ret = wasm.addedrobot_index(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * What each joystick on the board is called, in the order
     * `set_joystick_at` numbers them.
     * @returns {string[]}
     */
    joystickNames() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_joystickNames(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Where each of those is wired, as one line apiece -- `A0/A1, SW D2,
     * 3V3`, which is what the pad working it can say underneath.
     * @returns {string[]}
     */
    joystickWiring() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_joystickWiring(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The board it runs on, as [`MICROCONTROLLERS`] names it -- `undefined` for
     * a robot built without one, which has nothing to flash.
     * @returns {string | undefined}
     */
    get microcontroller() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_microcontroller(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * What building the robot had to approximate or skip, to be shown beside
     * the robot it is about.
     * @returns {string[]}
     */
    get notes() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_notes(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The PSRAM setting it was configured with, as [`psramOptions`] names it
     * -- `undefined` for a board that offers none.
     * @returns {string | undefined}
     */
    get psram() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_psram(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Take every other robot out of the scene, leaving this one alone in it --
     * and whatever objects the caller says to keep.
     *
     * A robot drop replaces the robot on screen rather than joining it, and
     * this is the engine's half of that: the `Articulated`s and the controllers
     * driving them -- each an emulated board still executing sixteen million
     * cycles a second whether or not anything is drawn for it.
     *
     * An *object* drop is the other way round and joins the scene, so the
     * objects already placed are not the robot's to clear away. They are
     * `Articulated`s like everything else, though -- a free body is a one-body
     * articulated -- so there is nothing in the scene that tells one from the
     * other, and the page, which holds a handle to each, says which indices are
     * objects. `keep` is those, and the indices they end up at come back in the
     * same order for the page to write onto its handles.
     *
     * It runs *after* [`RobotPlan::js_add_to`] rather than before, so a robot
     * that fails to build leaves the one already running alone. What that
     * costs is a renumbering: this robot was appended, and comes out at index
     * 0 with the kept objects after it in their old order -- hence `&mut self`,
     * since every panel on the page addresses its robot by the index on this
     * handle.
     *
     * The visuals are the caller's half, and have to be stripped *before* this
     * is called: `addArticulatedVisuals` in `www/src/index.ts` made them and
     * only it knows their names, and a name is `frame_articulated_body`, which
     * needs the articulated that is about to go.
     * @param {InterfaceHybrid} hybrid
     * @param {Uint32Array} keep
     * @returns {Uint32Array}
     */
    removeOtherRobots(hybrid, keep) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            _assertClass(hybrid, InterfaceHybrid);
            const ptr0 = passArray32ToWasm0(keep, wasm.__wbindgen_export);
            const len0 = WASM_VECTOR_LEN;
            wasm.addedrobot_removeOtherRobots(retptr, this.__wbg_ptr, hybrid.__wbg_ptr, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v2 = getArrayU32FromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v2;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Put the robot back where it started, at rest.
     *
     * This is not `Hybrid::reset`, which sets every joint to zero -- including
     * the floating root's pose. The robot's own joints do start at zero, but
     * its root was lifted clear of the ground, so resetting to zero would drop
     * it through the floor. Running a sketch starts here, so the same firmware
     * twice does the same thing twice.
     * @param {InterfaceHybrid} hybrid
     */
    restart(hybrid) {
        _assertClass(hybrid, InterfaceHybrid);
        wasm.addedrobot_restart(this.__wbg_ptr, hybrid.__wbg_ptr);
    }
    /**
     * What each switch on the board is called, in the order `set_switch_at`
     * numbers them.
     * @returns {string[]}
     */
    switchNames() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_switchNames(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Where each of those switches is wired, as one line apiece -- `D2 to
     * GND`, which is what the button pressing it can say underneath.
     * @returns {string[]}
     */
    switchWiring() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.addedrobot_switchWiring(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
}
if (Symbol.dispose) AddedRobot.prototype[Symbol.dispose] = AddedRobot.prototype.free;

/**
 * A folder that was dropped onto the page, read into memory.
 *
 * Paths are relative to the drop root and keep the dropped folder's own name as
 * their first segment: dropping `so101/` gives paths like
 * `so101/meshes/base.stl`. Anything named in [`SKIPPED_NAMES`] is not in here.
 */
export class DroppedFolder {
    static __wrap(ptr) {
        const obj = Object.create(DroppedFolder.prototype);
        obj.__wbg_ptr = ptr;
        DroppedFolderFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DroppedFolderFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_droppedfolder_free(ptr, 0);
    }
    /**
     * @param {string} path
     * @returns {Uint8Array | undefined}
     */
    bytes(path) {
        const ptr0 = passStringToWasm0(path, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.droppedfolder_bytes(this.__wbg_ptr, ptr0, len0);
        return takeObject(ret);
    }
    /**
     * @returns {number}
     */
    get fileCount() {
        const ret = wasm.droppedfolder_fileCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.droppedfolder_name(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * @returns {string[]}
     */
    paths() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.droppedfolder_paths(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * What Rust made of the folder, as a human-readable report.
     * @returns {string}
     */
    report() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.droppedfolder_report(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * @param {string} path
     * @returns {string | undefined}
     */
    text(path) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(path, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.droppedfolder_text(retptr, this.__wbg_ptr, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v2;
            if (r0 !== 0) {
                v2 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v2;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * @returns {number}
     */
    get totalBytes() {
        const ret = wasm.droppedfolder_totalBytes(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) DroppedFolder.prototype[Symbol.dispose] = DroppedFolder.prototype.free;

export class InterfaceCloth {
    static __wrap(ptr) {
        const obj = Object.create(InterfaceCloth.prototype);
        obj.__wbg_ptr = ptr;
        InterfaceClothFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InterfaceClothFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_interfacecloth_free(ptr, 0);
    }
    /**
     * @returns {Uint32Array}
     */
    faces() {
        const ret = wasm.interfacecloth_faces(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @param {number} dt
     */
    step(dt) {
        wasm.interfacecloth_step(this.__wbg_ptr, dt);
    }
    /**
     * @returns {Float32Array}
     */
    vertices() {
        const ret = wasm.interfacecloth_vertices(this.__wbg_ptr);
        return takeObject(ret);
    }
}
if (Symbol.dispose) InterfaceCloth.prototype[Symbol.dispose] = InterfaceCloth.prototype.free;

export class InterfaceController {
    static __wrap(ptr) {
        const obj = Object.create(InterfaceController.prototype);
        obj.__wbg_ptr = ptr;
        InterfaceControllerFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InterfaceControllerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_interfacecontroller_free(ptr, 0);
    }
}
if (Symbol.dispose) InterfaceController.prototype[Symbol.dispose] = InterfaceController.prototype.free;

export class InterfaceFEMDeformable {
    static __wrap(ptr) {
        const obj = Object.create(InterfaceFEMDeformable.prototype);
        obj.__wbg_ptr = ptr;
        InterfaceFEMDeformableFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InterfaceFEMDeformableFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_interfacefemdeformable_free(ptr, 0);
    }
    /**
     * @returns {Uint32Array}
     */
    facets() {
        const ret = wasm.interfacefemdeformable_facets(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @param {number} dt
     * @param {Float64Array} tau
     * @returns {Promise<void>}
     */
    step(dt, tau) {
        const ptr0 = passArrayF64ToWasm0(tau, wasm.__wbindgen_export);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.interfacefemdeformable_step(this.__wbg_ptr, dt, ptr0, len0);
        return takeObject(ret);
    }
    /**
     * @returns {Float64Array}
     */
    vertices() {
        const ret = wasm.interfacefemdeformable_vertices(this.__wbg_ptr);
        return takeObject(ret);
    }
}
if (Symbol.dispose) InterfaceFEMDeformable.prototype[Symbol.dispose] = InterfaceFEMDeformable.prototype.free;

export class InterfaceFluid2D {
    static __wrap(ptr) {
        const obj = Object.create(InterfaceFluid2D.prototype);
        obj.__wbg_ptr = ptr;
        InterfaceFluid2DFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InterfaceFluid2DFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_interfacefluid2d_free(ptr, 0);
    }
    /**
     * @returns {Float64Array}
     */
    particles() {
        const ret = wasm.interfacefluid2d_particles(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @param {number} dt
     */
    step(dt) {
        wasm.interfacefluid2d_step(this.__wbg_ptr, dt);
    }
}
if (Symbol.dispose) InterfaceFluid2D.prototype[Symbol.dispose] = InterfaceFluid2D.prototype.free;

export class InterfaceHybrid {
    static __wrap(ptr) {
        const obj = Object.create(InterfaceHybrid.prototype);
        obj.__wbg_ptr = ptr;
        InterfaceHybridFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InterfaceHybridFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_interfacehybrid_free(ptr, 0);
    }
    /**
     * Pull what articulated `i` is being held by towards a different world
     * point -- a cursor that has moved. Nothing held, nothing to aim.
     * @param {number} i
     * @param {number} tx
     * @param {number} ty
     * @param {number} tz
     */
    aim_applied_spring_at(i, tx, ty, tz) {
        wasm.interfacehybrid_aim_applied_spring_at(this.__wbg_ptr, i, tx, ty, tz);
    }
    /**
     * What camera `camera` on the board running articulated `i` wants drawn
     * for it: `[format, width, height, fps, vertical_flip]`, or empty while
     * it wants nothing. Format is 1 for JPEG, 2 for RGB565, 3 for grayscale;
     * `vertical_flip` is 1 when the sensor reverses scan lines.
     *
     * Polled, like `hardware_warning_at`: what a sensor is configured to
     * produce is the running firmware's doing, and changes when it calls
     * `esp_camera_init` -- or when a new sketch is flashed.
     * @param {number} i
     * @param {number} camera
     * @returns {Uint32Array}
     */
    camera_request_at(i, camera) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacehybrid_camera_request_at(retptr, this.__wbg_ptr, i, camera);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayU32FromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Let go of articulated `i`. A step does not spend a hold, so this is the
     * only thing that ends it.
     * @param {number} i
     */
    clear_applied_spring_at(i) {
        wasm.interfacehybrid_clear_applied_spring_at(this.__wbg_ptr, i);
    }
    /**
     * @returns {Uint32Array}
     */
    cloth_dofs() {
        const ret = wasm.interfacehybrid_cloth_dofs(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * number of faces of each cloth
     * @returns {Uint32Array}
     */
    cloth_face_ns() {
        const ret = wasm.interfacehybrid_cloth_face_ns(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @returns {Uint32Array}
     */
    cloth_faces() {
        const ret = wasm.interfacehybrid_cloth_faces(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @returns {Float32Array}
     */
    cloth_nodes() {
        const ret = wasm.interfacehybrid_cloth_nodes(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * dofs of each deformable as an array
     * @returns {Uint32Array}
     */
    deformable_dofs() {
        const ret = wasm.interfacehybrid_deformable_dofs(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * number of faces of each deformable
     * @returns {Uint32Array}
     */
    deformable_face_ns() {
        const ret = wasm.interfacehybrid_deformable_face_ns(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @returns {Uint32Array}
     */
    deformable_faces() {
        const ret = wasm.interfacehybrid_deformable_faces(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @returns {Float32Array}
     */
    deformable_nodes() {
        const ret = wasm.interfacehybrid_deformable_nodes(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * Weld body `body` of articulated `i` to the world where it stands.
     *
     * The other thing a caller outside the physics can do to a body, and the
     * opposite of `set_applied_spring_at` in every respect: that one is a force
     * and so only ever holds a body approximately, while this is a constraint
     * and holds it exactly. Which is why it takes no pose -- the body is
     * already in the one it is being fixed in, and a body whose velocity is
     * held at zero stays there.
     * @param {number} i
     * @param {number} body
     */
    fix_body_at(i, body) {
        wasm.interfacehybrid_fix_body_at(this.__wbg_ptr, i, body);
    }
    /**
     * @param {number} i
     * @param {number} j
     * @returns {string}
     */
    frame_articulated_body(i, j) {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacehybrid_frame_articulated_body(retptr, this.__wbg_ptr, i, j);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * @returns {string}
     */
    get_uart() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacehybrid_get_uart(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * UART output of the controller running articulated `i`.
     *
     * `get_uart` above answers for the first articulated, which is all a scene
     * built around one robot needs. A scene that gains robots at runtime has to
     * say which one it means -- as `reboot_code_controller` already does.
     * @param {number} i
     * @returns {string}
     */
    get_uart_at(i) {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacehybrid_get_uart_at(retptr, this.__wbg_ptr, i);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Vecs of 4-element (normal, distance)
     * @returns {Float32Array}
     */
    halfspaces() {
        const ret = wasm.interfacehybrid_halfspaces(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * What the hardware running articulated `i` has to warn about, if
     * anything. Polled: it depends on what the firmware has done to its pins,
     * so it changes as the sketch runs rather than at any one moment.
     * @param {number} i
     * @returns {string | undefined}
     */
    hardware_warning_at(i) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacehybrid_hardware_warning_at(retptr, this.__wbg_ptr, i);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * @param {number} i
     * @param {number} body
     * @returns {boolean}
     */
    is_body_fixed_at(i, body) {
        const ret = wasm.interfacehybrid_is_body_fixed_at(this.__wbg_ptr, i, body);
        return ret !== 0;
    }
    /**
     * @param {number} i
     * @param {number} j
     * @param {number} k
     * @returns {Float32Array}
     */
    iso_visual_to_body(i, j, k) {
        const ret = wasm.interfacehybrid_iso_visual_to_body(this.__wbg_ptr, i, j, k);
        return takeObject(ret);
    }
    /**
     * @returns {Float32Array}
     */
    joint_q() {
        const ret = wasm.interfacehybrid_joint_q(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @returns {number}
     */
    n_articulated() {
        const ret = wasm.interfacehybrid_n_articulated(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * number of bodies in articulated i
     * @param {number} i
     * @returns {number}
     */
    n_bodies_articulated(i) {
        const ret = wasm.interfacehybrid_n_bodies_articulated(this.__wbg_ptr, i);
        return ret >>> 0;
    }
    /**
     * @returns {number}
     */
    n_static_bodies() {
        const ret = wasm.interfacehybrid_n_static_bodies(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @param {number} i
     * @param {number} j
     * @returns {number}
     */
    n_visuals_articulated_body(i, j) {
        const ret = wasm.interfacehybrid_n_visuals_articulated_body(this.__wbg_ptr, i, j);
        return ret >>> 0;
    }
    /**
     * Connection `id` of articulated `i` is gone, however it went.
     * @param {number} i
     * @param {number} id
     */
    net_closed_at(i, id) {
        wasm.interfacehybrid_net_closed_at(this.__wbg_ptr, i, id);
    }
    /**
     * A message has arrived on connection `id` of articulated `i`.
     * @param {number} i
     * @param {number} id
     * @param {boolean} text
     * @param {Uint8Array} data
     */
    net_message_at(i, id, text, data) {
        const ptr0 = passArray8ToWasm0(data, wasm.__wbindgen_export);
        const len0 = WASM_VECTOR_LEN;
        wasm.interfacehybrid_net_message_at(this.__wbg_ptr, i, id, text, ptr0, len0);
    }
    /**
     * Connection `id` on articulated `i` is up. Not to be sent before it
     * really is: the firmware answers its own handshake on the strength of
     * this.
     * @param {number} i
     * @param {number} id
     */
    net_opened_at(i, id) {
        wasm.interfacehybrid_net_opened_at(this.__wbg_ptr, i, id);
    }
    /**
     * The payload to send, for a request of kind 1.
     * @param {number} n
     * @returns {Uint8Array}
     */
    net_request_data(n) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacehybrid_net_request_data(retptr, this.__wbg_ptr, n);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayU8FromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 1, 1);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The host to open, for a request of kind 0.
     * @param {number} n
     * @returns {string}
     */
    net_request_host(n) {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacehybrid_net_request_host(retptr, this.__wbg_ptr, n);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The connection request `n` is about. Ids are never reused, so one the
     * host has forgotten stays forgotten.
     * @param {number} n
     * @returns {number}
     */
    net_request_id(n) {
        const ret = wasm.interfacehybrid_net_request_id(this.__wbg_ptr, n);
        return ret >>> 0;
    }
    /**
     * Whether request `n`'s payload is text rather than binary.
     * @param {number} n
     * @returns {boolean}
     */
    net_request_is_text(n) {
        const ret = wasm.interfacehybrid_net_request_is_text(this.__wbg_ptr, n);
        return ret !== 0;
    }
    /**
     * Which kind of request `n` is: 0 to open, 1 to send, 2 to close.
     * @param {number} n
     * @returns {number}
     */
    net_request_kind(n) {
        const ret = wasm.interfacehybrid_net_request_kind(this.__wbg_ptr, n);
        return ret >>> 0;
    }
    /**
     * The path to ask for, for a request of kind 0.
     * @param {number} n
     * @returns {string}
     */
    net_request_path(n) {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacehybrid_net_request_path(retptr, this.__wbg_ptr, n);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The port to open, for a request of kind 0.
     * @param {number} n
     * @returns {number}
     */
    net_request_port(n) {
        const ret = wasm.interfacehybrid_net_request_port(this.__wbg_ptr, n);
        return ret >>> 0;
    }
    /**
     * Take everything the board running articulated `i` wants of the
     * network, and say how many things that was.
     *
     * A request is several values of different types -- a host, a port, a
     * payload -- so it is read back by the accessors below rather than
     * returned whole; `wasm_bindgen` has no way to hand over a list of
     * mixed records, and inventing a wire format for one caller would be a
     * second thing to keep in step. The requests stay here until the next
     * call takes their place.
     * @param {number} i
     * @returns {number}
     */
    net_take_requests_at(i) {
        const ret = wasm.interfacehybrid_net_take_requests_at(this.__wbg_ptr, i);
        return ret >>> 0;
    }
    /**
     * @param {number} i
     * @param {number} j
     * @returns {Float32Array}
     */
    pose_articulated_body(i, j) {
        const ret = wasm.interfacehybrid_pose_articulated_body(this.__wbg_ptr, i, j);
        return takeObject(ret);
    }
    /**
     * The names of the phases `profile_take` reports, comma-separated and in
     * the same order, so a caller never writes that order down a second time.
     * @returns {string}
     */
    profile_labels() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacehybrid_profile_labels(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * What each phase of `step` has cost since this was last called: the
     * number of steps counted, then milliseconds per phase in `profile_labels`
     * order.
     *
     * Reading resets, so successive calls carve the run into windows -- poll
     * it on an interval and each answer describes the interval that just
     * ended. The step count leads because it is what makes the rest readable:
     * the same 8 ms means something different over 30 steps than over 3000,
     * and a window with no steps in it (a paused or backgrounded tab) is all
     * zeroes rather than a division by zero.
     * @returns {Float32Array}
     */
    profile_take() {
        const ret = wasm.interfacehybrid_profile_take(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @param {number} i
     * @param {string} code
     */
    reboot_code_controller(i, code) {
        const ptr0 = passStringToWasm0(code, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.interfacehybrid_reboot_code_controller(this.__wbg_ptr, i, ptr0, len0);
    }
    /**
     * @param {number} i
     * @param {Uint8Array} app_bin
     * @param {string} symbols
     */
    reboot_esp32_controller(i, app_bin, symbols) {
        const ptr0 = passArray8ToWasm0(app_bin, wasm.__wbindgen_export);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(symbols, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len1 = WASM_VECTOR_LEN;
        wasm.interfacehybrid_reboot_esp32_controller(this.__wbg_ptr, i, ptr0, len0, ptr1, len1);
    }
    /**
     * Let body `body` of articulated `i` go again, back under gravity.
     * @param {number} i
     * @param {number} body
     */
    release_body_at(i, body) {
        wasm.interfacehybrid_release_body_at(this.__wbg_ptr, i, body);
    }
    reset() {
        wasm.interfacehybrid_reset(this.__wbg_ptr);
    }
    /**
     * @param {string} payload
     */
    send_uart(payload) {
        const ptr0 = passStringToWasm0(payload, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.interfacehybrid_send_uart(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Send `payload` to the controller running articulated `i`, as typing into
     * a serial monitor does.
     *
     * The counterpart of `get_uart_at`, and there for the same reason: a scene
     * that gains robots at runtime has to say which board it means.
     * @param {number} i
     * @param {string} payload
     */
    send_uart_at(i, payload) {
        const ptr0 = passStringToWasm0(payload, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.interfacehybrid_send_uart_at(this.__wbg_ptr, i, ptr0, len0);
    }
    /**
     * Take hold of body `body` of articulated `i` at world point `p`, pulling
     * it towards world point `t` with a damped spring, and keep hold until it
     * is aimed somewhere else or let go.
     *
     * For a caller outside the physics -- a cursor dragging a link -- which is
     * why it is a spring and not a constraint: what the body does with it is
     * still the solve's answer, so a link held by its servos barely moves and a
     * loose one swings. The gains and the cap come from that caller, since it
     * is the one deciding how firm a hand this is. What the solve owns is the
     * force, and it has to: the caller runs at the display's frame rate, and
     * both where the body is and how fast it is going are sixty steps different
     * by the time it says anything. Hence a point *taken hold of* here, kept as
     * a material point, rather than one pushed in every frame.
     * @param {number} i
     * @param {number} body
     * @param {number} px
     * @param {number} py
     * @param {number} pz
     * @param {number} tx
     * @param {number} ty
     * @param {number} tz
     * @param {number} stiffness
     * @param {number} damping
     * @param {number} max_force
     */
    set_applied_spring_at(i, body, px, py, pz, tx, ty, tz, stiffness, damping, max_force) {
        wasm.interfacehybrid_set_applied_spring_at(this.__wbg_ptr, i, body, px, py, pz, tx, ty, tz, stiffness, damping, max_force);
    }
    /**
     * @param {number} i
     * @param {number} value
     */
    set_joint_q(i, value) {
        wasm.interfacehybrid_set_joint_q(this.__wbg_ptr, i, value);
    }
    /**
     * @param {number} i
     * @param {number} value
     */
    set_joint_v(i, value) {
        wasm.interfacehybrid_set_joint_v(this.__wbg_ptr, i, value);
    }
    /**
     * Hold joystick `joystick` on the board running articulated `i` at
     * `x`, `y`, with its cap switch pressed or not.
     *
     * The whole state of the stick every time, unlike `set_switch_at`'s one
     * bit: a pointer dragging a pad reports where it is rather than what
     * changed, and the controller is what works out which pins that moves.
     * @param {number} i
     * @param {number} joystick
     * @param {number} x
     * @param {number} y
     * @param {boolean} pressed
     */
    set_joystick_at(i, joystick, x, y, pressed) {
        wasm.interfacehybrid_set_joystick_at(this.__wbg_ptr, i, joystick, x, y, pressed);
    }
    /**
     * Press or release switch `switch` on the board running articulated `i`.
     *
     * The press is held until it is released: the controller drives the pin
     * from outside the chip, exactly as a finger holding a button down does.
     * @param {number} i
     * @param {number} _switch
     * @param {boolean} pressed
     */
    set_switch_at(i, _switch, pressed) {
        wasm.interfacehybrid_set_switch_at(this.__wbg_ptr, i, _switch, pressed);
    }
    /**
     * @param {number} i
     * @returns {Uint32Array}
     */
    static_body_faces(i) {
        const ret = wasm.interfacehybrid_static_body_faces(this.__wbg_ptr, i);
        return takeObject(ret);
    }
    /**
     * @param {number} i
     * @returns {Float32Array}
     */
    static_body_vertices(i) {
        const ret = wasm.interfacehybrid_static_body_vertices(this.__wbg_ptr, i);
        return takeObject(ret);
    }
    /**
     * @param {number} i
     * @returns {boolean}
     */
    static_visual_show(i) {
        const ret = wasm.interfacehybrid_static_visual_show(this.__wbg_ptr, i);
        return ret !== 0;
    }
    /**
     * @param {number} dt
     * @param {Float64Array} input
     */
    step(dt, input) {
        const ptr0 = passArrayF64ToWasm0(input, wasm.__wbindgen_export);
        const len0 = WASM_VECTOR_LEN;
        wasm.interfacehybrid_step(this.__wbg_ptr, dt, ptr0, len0);
    }
    /**
     * @param {number} dt
     * @param {Float64Array} input
     */
    step_dual(dt, input) {
        const ptr0 = passArrayF64ToWasm0(input, wasm.__wbindgen_export);
        const len0 = WASM_VECTOR_LEN;
        wasm.interfacehybrid_step_dual(this.__wbg_ptr, dt, ptr0, len0);
    }
    /**
     * Hand camera `camera` on the board running articulated `i` its next
     * picture, as tightly packed RGBA (`width * height * 4`, top row first)
     * at the size the current request asked for. The controller packs it
     * into whatever its sensor puts on the wire; the firmware reads it out
     * through its own camera driver.
     * @param {number} i
     * @param {number} camera
     * @param {Uint8Array} rgba
     */
    supply_camera_frame_at(i, camera, rgba) {
        const ptr0 = passArray8ToWasm0(rgba, wasm.__wbindgen_export);
        const len0 = WASM_VECTOR_LEN;
        wasm.interfacehybrid_supply_camera_frame_at(this.__wbg_ptr, i, camera, ptr0, len0);
    }
    /**
     * @returns {number}
     */
    time() {
        const ret = wasm.interfacehybrid_time(this.__wbg_ptr);
        return ret;
    }
    /**
     * Return color, or default
     * @param {number} i
     * @param {number} j
     * @param {number} k
     * @returns {Float32Array}
     */
    visual_color(i, j, k) {
        const ret = wasm.interfacehybrid_visual_color(this.__wbg_ptr, i, j, k);
        return takeObject(ret);
    }
    /**
     * @param {number} i
     * @param {number} j
     * @param {number} k
     * @returns {Float32Array}
     */
    visual_cuboid_wdh(i, j, k) {
        const ret = wasm.interfacehybrid_visual_cuboid_wdh(this.__wbg_ptr, i, j, k);
        return takeObject(ret);
    }
    /**
     * @param {number} i
     * @param {number} j
     * @param {number} k
     * @returns {Uint32Array}
     */
    visual_mesh_faces(i, j, k) {
        const ret = wasm.interfacehybrid_visual_mesh_faces(this.__wbg_ptr, i, j, k);
        return takeObject(ret);
    }
    /**
     * The colour of each group `visual_mesh_groups` returned, as RGB triples.
     * @param {number} i
     * @param {number} j
     * @param {number} k
     * @returns {Float32Array}
     */
    visual_mesh_group_colors(i, j, k) {
        const ret = wasm.interfacehybrid_visual_mesh_group_colors(this.__wbg_ptr, i, j, k);
        return takeObject(ret);
    }
    /**
     * The mesh's material groups as `[start, count]` pairs, counted in *faces*.
     *
     * Empty for a mesh drawn in one colour -- i.e. anything but an OBJ that
     * named materials with `usemtl` -- in which case `visual_color` is the
     * whole story. When it is not empty the pairs cover every face, so the
     * renderer can hand each run its own material and still draw the mesh
     * whole. The mesh itself is undivided: one set of vertices, one collider.
     * @param {number} i
     * @param {number} j
     * @param {number} k
     * @returns {Uint32Array}
     */
    visual_mesh_groups(i, j, k) {
        const ret = wasm.interfacehybrid_visual_mesh_groups(this.__wbg_ptr, i, j, k);
        return takeObject(ret);
    }
    /**
     * @param {number} i
     * @param {number} j
     * @param {number} k
     * @returns {Float32Array}
     */
    visual_mesh_vertices(i, j, k) {
        const ret = wasm.interfacehybrid_visual_mesh_vertices(this.__wbg_ptr, i, j, k);
        return takeObject(ret);
    }
    /**
     * @param {number} i
     * @returns {boolean}
     */
    visual_show(i) {
        const ret = wasm.interfacehybrid_visual_show(this.__wbg_ptr, i);
        return ret !== 0;
    }
    /**
     * @param {number} i
     * @param {number} j
     * @param {number} k
     * @returns {number}
     */
    visual_sphere_r(i, j, k) {
        const ret = wasm.interfacehybrid_visual_sphere_r(this.__wbg_ptr, i, j, k);
        return ret;
    }
    /**
     * @param {number} i
     * @param {number} j
     * @param {number} k
     * @returns {number}
     */
    visual_type(i, j, k) {
        const ret = wasm.interfacehybrid_visual_type(this.__wbg_ptr, i, j, k);
        return ret >>> 0;
    }
}
if (Symbol.dispose) InterfaceHybrid.prototype[Symbol.dispose] = InterfaceHybrid.prototype.free;

export class InterfaceMPMDeformable {
    static __wrap(ptr) {
        const obj = Object.create(InterfaceMPMDeformable.prototype);
        obj.__wbg_ptr = ptr;
        InterfaceMPMDeformableFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InterfaceMPMDeformableFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_interfacempmdeformable_free(ptr, 0);
    }
    /**
     * @returns {Float64Array}
     */
    particles() {
        const ret = wasm.interfacempmdeformable_particles(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @param {number} dt
     */
    step(dt) {
        wasm.interfacempmdeformable_step(this.__wbg_ptr, dt);
    }
}
if (Symbol.dispose) InterfaceMPMDeformable.prototype[Symbol.dispose] = InterfaceMPMDeformable.prototype.free;

export class InterfaceMassSpring {
    static __wrap(ptr) {
        const obj = Object.create(InterfaceMassSpring.prototype);
        obj.__wbg_ptr = ptr;
        InterfaceMassSpringFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InterfaceMassSpringFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_interfacemassspring_free(ptr, 0);
    }
    /**
     * @returns {Uint32Array}
     */
    facets() {
        const ret = wasm.interfacemassspring_facets(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @returns {Float32Array}
     */
    nodes() {
        const ret = wasm.interfacemassspring_nodes(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @param {number} dt
     */
    step(dt) {
        wasm.interfacemassspring_step(this.__wbg_ptr, dt);
    }
}
if (Symbol.dispose) InterfaceMassSpring.prototype[Symbol.dispose] = InterfaceMassSpring.prototype.free;

export class InterfaceMassSpringDeformable {
    static __wrap(ptr) {
        const obj = Object.create(InterfaceMassSpringDeformable.prototype);
        obj.__wbg_ptr = ptr;
        InterfaceMassSpringDeformableFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InterfaceMassSpringDeformableFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_interfacemassspringdeformable_free(ptr, 0);
    }
    /**
     * @returns {Uint32Array}
     */
    edges() {
        const ret = wasm.interfacemassspringdeformable_edges(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @returns {Uint32Array}
     */
    facets() {
        const ret = wasm.interfacemassspringdeformable_facets(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @param {number} dt
     * @param {Float64Array} tau
     */
    step(dt, tau) {
        const ptr0 = passArrayF64ToWasm0(tau, wasm.__wbindgen_export);
        const len0 = WASM_VECTOR_LEN;
        wasm.interfacemassspringdeformable_step(this.__wbg_ptr, dt, ptr0, len0);
    }
    /**
     * @returns {Uint32Array}
     */
    tetrahedra() {
        const ret = wasm.interfacemassspringdeformable_tetrahedra(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @returns {Float64Array}
     */
    vertices() {
        const ret = wasm.interfacemassspringdeformable_vertices(this.__wbg_ptr);
        return takeObject(ret);
    }
}
if (Symbol.dispose) InterfaceMassSpringDeformable.prototype[Symbol.dispose] = InterfaceMassSpringDeformable.prototype.free;

/**
 * WebAssembly interface to the MechanismState struct.
 */
export class InterfaceMechanismState {
    static __wrap(ptr) {
        const obj = Object.create(InterfaceMechanismState.prototype);
        obj.__wbg_ptr = ptr;
        InterfaceMechanismStateFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InterfaceMechanismStateFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_interfacemechanismstate_free(ptr, 0);
    }
    /**
     * @param {Float64Array} normal
     * @param {number} distance
     */
    addHalfSpace(normal, distance) {
        const ptr0 = passArrayF64ToWasm0(normal, wasm.__wbindgen_export);
        const len0 = WASM_VECTOR_LEN;
        wasm.interfacemechanismstate_addHalfSpace(this.__wbg_ptr, ptr0, len0, distance);
    }
    /**
     * @param {Float64Array} normal
     * @param {number} distance
     * @param {number} alpha
     * @param {number} mu
     */
    addHalfSpaceCustom(normal, distance, alpha, mu) {
        const ptr0 = passArrayF64ToWasm0(normal, wasm.__wbindgen_export);
        const len0 = WASM_VECTOR_LEN;
        wasm.interfacemechanismstate_addHalfSpaceCustom(this.__wbg_ptr, ptr0, len0, distance, alpha, mu);
    }
    /**
     * Get the positions of each contact point in the system
     * @returns {Float64Array}
     */
    contact_positions() {
        const ret = wasm.interfacemechanismstate_contact_positions(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * Get the isometry of the visual mesh on a body, which is also the isometry of the body itself
     * TODO: take the isometry from body directly instead
     * @param {number} body_index
     * @returns {Float64Array}
     */
    isometry(body_index) {
        const ret = wasm.interfacemechanismstate_isometry(this.__wbg_ptr, body_index);
        return takeObject(ret);
    }
    /**
     * Get the poses of each body in the system
     * @returns {Float64Array}
     */
    poses() {
        const ret = wasm.interfacemechanismstate_poses(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @param {number} body_index
     * @returns {Float64Array}
     */
    sphere_center(body_index) {
        const ret = wasm.interfacemechanismstate_sphere_center(this.__wbg_ptr, body_index);
        return takeObject(ret);
    }
    /**
     * Get the vertices of the collider mesh on a body
     * @param {number} body_index
     * @returns {Float64Array}
     */
    vertices(body_index) {
        const ret = wasm.interfacemechanismstate_vertices(this.__wbg_ptr, body_index);
        return takeObject(ret);
    }
    /**
     * Get the base vertices of the collider mesh on a body
     * @param {number} body_index
     * @returns {Float32Array[]}
     */
    visual_base_vertices(body_index) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacemechanismstate_visual_base_vertices(retptr, this.__wbg_ptr, body_index);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Get the vertices of the visual mesh on a body
     * @param {number} body_index
     * @returns {Uint32Array[]}
     */
    visual_facets(body_index) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacemechanismstate_visual_facets(retptr, this.__wbg_ptr, body_index);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
}
if (Symbol.dispose) InterfaceMechanismState.prototype[Symbol.dispose] = InterfaceMechanismState.prototype.free;

export class InterfaceSimulator {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InterfaceSimulatorFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_interfacesimulator_free(ptr, 0);
    }
    /**
     * @returns {Float64Array}
     */
    contact_positions() {
        const ret = wasm.interfacesimulator_contact_positions(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * Get the faces of the collider mesh on a body
     * @param {number} body_index
     * @returns {Uint32Array[]}
     */
    facets(body_index) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacesimulator_facets(retptr, this.__wbg_ptr, body_index);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Get the isometry of the collider mesh on a body
     * @param {number} body_index
     * @returns {Float64Array}
     */
    isometry(body_index) {
        const ret = wasm.interfacesimulator_isometry(this.__wbg_ptr, body_index);
        return takeObject(ret);
    }
    /**
     * @param {InterfaceMechanismState} state
     * @param {InterfaceController} controller
     */
    constructor(state, controller) {
        _assertClass(state, InterfaceMechanismState);
        var ptr0 = state.__destroy_into_raw();
        _assertClass(controller, InterfaceController);
        var ptr1 = controller.__destroy_into_raw();
        const ret = wasm.interfacesimulator_new(ptr0, ptr1);
        this.__wbg_ptr = ret;
        InterfaceSimulatorFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * @returns {Float64Array}
     */
    poses() {
        const ret = wasm.interfacesimulator_poses(this.__wbg_ptr);
        return takeObject(ret);
    }
    /**
     * @param {number} body_index
     * @returns {Float64Array}
     */
    sphere_center(body_index) {
        const ret = wasm.interfacesimulator_sphere_center(this.__wbg_ptr, body_index);
        return takeObject(ret);
    }
    /**
     * @param {number} dt
     * @param {Float64Array} control_input
     * @returns {Float64Array}
     */
    step(dt, control_input) {
        const ptr0 = passArrayF64ToWasm0(control_input, wasm.__wbindgen_export);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.interfacesimulator_step(this.__wbg_ptr, dt, ptr0, len0);
        return takeObject(ret);
    }
    /**
     * Get the vertices of the collider mesh on a body
     * @param {number} body_index
     * @returns {Float64Array}
     */
    vertices(body_index) {
        const ret = wasm.interfacesimulator_vertices(this.__wbg_ptr, body_index);
        return takeObject(ret);
    }
    /**
     * Get the vertices of the collider mesh on a body
     * @param {number} body_index
     * @returns {Float32Array[]}
     */
    visual_base_vertices(body_index) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.interfacesimulator_visual_base_vertices(retptr, this.__wbg_ptr, body_index);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
}
if (Symbol.dispose) InterfaceSimulator.prototype[Symbol.dispose] = InterfaceSimulator.prototype.free;

/**
 * A dropped robot waiting on its hardware before it is built and simulated.
 */
export class RobotPlan {
    static __wrap(ptr) {
        const obj = Object.create(RobotPlan.prototype);
        obj.__wbg_ptr = ptr;
        RobotPlanFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        RobotPlanFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_robotplan_free(ptr, 0);
    }
    /**
     * The actuator fitted to a joint, if one has been.
     * @param {number} joint
     * @returns {string | undefined}
     */
    actuatorName(joint) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_actuatorName(retptr, this.__wbg_ptr, joint);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Add a part to the board, returning where it landed in the list.
     *
     * It arrives named after what it is and wired to whatever pins are going,
     * so a switch is usable the moment it is added and the user only has to
     * touch what they want different.
     * @param {string} component
     * @returns {number}
     */
    addComponent(component) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(component, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_addComponent(retptr, this.__wbg_ptr, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var r2 = getDataViewMemory0().getInt32(retptr + 4 * 2, true);
            if (r2) {
                throw takeObject(r1);
            }
            return r0 >>> 0;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Build the configured robot, put it into a scene that is already running,
     * and switch its microcontroller on.
     *
     * Hands back an [`AddedRobot`]: where the robot landed, so the caller can
     * create its visuals, and the pose it started in, so a later run can put it
     * back there. The simulation loop picks it up from the next step onwards.
     *
     * **It borrows the plan rather than spending it**, which is what lets the
     * page re-open the configuration panel on a robot that is already running
     * and build it again from the edited answers. Rebuilding is not a choice
     * there: a joint carries its actuator's armature and dry friction, and both
     * go on at construction, so changing the hardware means a new
     * `Articulated`. Everything this reads is read afresh each time -- the
     * meshes included, which is why the plan keeps the [`DroppedFolder`] for as
     * long as the robot it describes is in the scene. Taking the old robot out
     * is the caller's half, through [`AddedRobot::js_remove_other_robots`].
     * @param {InterfaceHybrid} hybrid
     * @returns {AddedRobot}
     */
    addTo(hybrid) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            _assertClass(hybrid, InterfaceHybrid);
            wasm.robotplan_addTo(retptr, this.__wbg_ptr, hybrid.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var r2 = getDataViewMemory0().getInt32(retptr + 4 * 2, true);
            if (r2) {
                throw takeObject(r1);
            }
            return AddedRobot.__wrap(r0);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The address the servo on a joint answers to on the bus, for one that is
     * on a bus -- `undefined` for an unfitted joint or a servo commanded by a
     * pulse, neither of which has an address to give.
     *
     * A read-back like [`Self::js_actuator_name`], and there for the same
     * reason: a saved configuration is written out of the plan rather than out
     * of the panel, which may never have been shown.
     * @param {number} joint
     * @returns {number | undefined}
     */
    busId(joint) {
        const ret = wasm.robotplan_busId(this.__wbg_ptr, joint);
        return ret === 0xFFFFFF ? undefined : ret;
    }
    /**
     * How many camera links the URDF draws, each of which needs a sensor
     * choosing.
     * @returns {number}
     */
    get cameraCount() {
        const ret = wasm.robotplan_cameraCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The URDF's name for one of those links -- today always
     * [`robot::CAMERA_LINK`], since that is the only name read as a camera,
     * but shown rather than assumed so the user can see which link is being
     * asked about.
     * @param {number} camera
     * @returns {string | undefined}
     */
    cameraLink(camera) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_cameraLink(retptr, this.__wbg_ptr, camera);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The sensor fitted to it, if one has been. The read-back a saved
     * configuration is written back out of, as for actuators and components.
     * @param {number} camera
     * @returns {string | undefined}
     */
    cameraModel(camera) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_cameraModel(retptr, this.__wbg_ptr, camera);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Take the actuator off a joint and forget what it was given, leaving the
     * question open again -- not the same as [`Self::js_set_free`], which
     * answers it.
     * @param {number} joint
     */
    clearActuator(joint) {
        wasm.robotplan_clearActuator(this.__wbg_ptr, joint);
    }
    /**
     * Take the motor on a joint off its driver, leaving it with none -- and
     * the driver gone, if it was the last motor on it.
     * @param {number} joint
     */
    clearMotorDriver(joint) {
        wasm.robotplan_clearMotorDriver(this.__wbg_ptr, joint);
    }
    /**
     * How many parts are on the board.
     * @returns {number}
     */
    get componentCount() {
        const ret = wasm.robotplan_componentCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * What kind of part this is, i.e. its entry in [`COMPONENTS`].
     * @param {number} component
     * @returns {string | undefined}
     */
    componentKind(component) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_componentKind(retptr, this.__wbg_ptr, component);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * What the user calls this one.
     * @param {number} component
     * @returns {string | undefined}
     */
    componentName(component) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_componentName(retptr, this.__wbg_ptr, component);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * What is worth saying about this kind of part that its wires do not,
     * for the wiring step to show under it.
     * @param {number} component
     * @returns {string | undefined}
     */
    componentNote(component) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_componentNote(retptr, this.__wbg_ptr, component);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The pins this wire may go to -- for a switch's rail leg, either rail.
     * @param {number} component
     * @param {number} wire
     * @returns {string[]}
     */
    componentPinOptions(component, wire) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_componentPinOptions(retptr, this.__wbg_ptr, component, wire);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * How many wires this part has, 0 if there is no such part.
     * @param {number} component
     * @returns {number}
     */
    componentWireCount(component) {
        const ret = wasm.robotplan_componentWireCount(this.__wbg_ptr, component);
        return ret >>> 0;
    }
    /**
     * @param {number} component
     * @param {number} wire
     * @returns {string | undefined}
     */
    componentWireName(component, wire) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_componentWireName(retptr, this.__wbg_ptr, component, wire);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * @param {number} component
     * @param {number} wire
     * @returns {string | undefined}
     */
    componentWirePin(component, wire) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_componentWirePin(retptr, this.__wbg_ptr, component, wire);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * How many motors it can drive, one per channel.
     * @param {number} driver
     * @returns {number}
     */
    driverChannelCount(driver) {
        const ret = wasm.robotplan_driverChannelCount(this.__wbg_ptr, driver);
        return ret >>> 0;
    }
    /**
     * The joint whose motor is on that channel, `undefined` for a free one.
     * @param {number} driver
     * @param {number} channel
     * @returns {number | undefined}
     */
    driverChannelJoint(driver, channel) {
        const ret = wasm.robotplan_driverChannelJoint(this.__wbg_ptr, driver, channel);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * The channel's name as the part labels it: `A`, `B`.
     * @param {number} driver
     * @param {number} channel
     * @returns {string | undefined}
     */
    driverChannelName(driver, channel) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_driverChannelName(retptr, this.__wbg_ptr, driver, channel);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * How many drivers are on the board.
     * @returns {number}
     */
    get driverCount() {
        const ret = wasm.robotplan_driverCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * What kind of driver this is, i.e. its entry in [`DRIVERS`].
     * @param {number} driver
     * @returns {string | undefined}
     */
    driverKind(driver) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_driverKind(retptr, this.__wbg_ptr, driver);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * What the user calls this one.
     * @param {number} driver
     * @returns {string | undefined}
     */
    driverName(driver) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_driverName(retptr, this.__wbg_ptr, driver);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * What the wiring step shows under it.
     * @param {number} driver
     * @returns {string | undefined}
     */
    driverNote(driver) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_driverNote(retptr, this.__wbg_ptr, driver);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The pins this wire may go to.
     * @param {number} driver
     * @param {number} wire
     * @returns {string[]}
     */
    driverPinOptions(driver, wire) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_driverPinOptions(retptr, this.__wbg_ptr, driver, wire);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * How many wires this driver has to the board: the shared ones, and two
     * for each channel a motor is on. A channel with nothing on it has none.
     * @param {number} driver
     * @returns {number}
     */
    driverWireCount(driver) {
        const ret = wasm.robotplan_driverWireCount(this.__wbg_ptr, driver);
        return ret >>> 0;
    }
    /**
     * @param {number} driver
     * @param {number} wire
     * @returns {string | undefined}
     */
    driverWireName(driver, wire) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_driverWireName(retptr, this.__wbg_ptr, driver, wire);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * @param {number} driver
     * @param {number} wire
     * @returns {string | undefined}
     */
    driverWirePin(driver, wire) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_driverWirePin(retptr, this.__wbg_ptr, driver, wire);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Whether a joint was left free, as opposed to fitted or not yet answered.
     * The read-back for [`Self::js_set_free`], as [`Self::js_actuator_name`] is
     * for fitting.
     * @param {number} joint
     * @returns {boolean}
     */
    isFree(joint) {
        const ret = wasm.robotplan_isFree(this.__wbg_ptr, joint);
        return ret !== 0;
    }
    /**
     * How many joints need an actuator choosing.
     * @returns {number}
     */
    get jointCount() {
        const ret = wasm.robotplan_jointCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * @param {number} joint
     * @returns {string | undefined}
     */
    jointName(joint) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_jointName(retptr, this.__wbg_ptr, joint);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The microcontroller chosen, if one has been.
     *
     * The three read-backs here are what lets the plan -- rather than the
     * panel's controls -- be the record of what was chosen: a configuration
     * loaded from a file writes straight into the plan, and both the panel and
     * the saved file are then filled from the same place.
     * @returns {string | undefined}
     */
    get microcontroller() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_microcontroller(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Which channel of that driver it is on.
     * @param {number} joint
     * @returns {number | undefined}
     */
    motorChannel(joint) {
        const ret = wasm.robotplan_motorChannel(this.__wbg_ptr, joint);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * Which driver the motor on a joint is behind, as an index into the
     * drivers -- `undefined` for a joint on none. The read-back a saved
     * configuration is written out of, as [`Self::js_bus_id`] is.
     * @param {number} joint
     * @returns {number | undefined}
     */
    motorDriver(joint) {
        const ret = wasm.robotplan_motorDriver(this.__wbg_ptr, joint);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * The speed the gearmotor on a joint was bought at, in rpm at its rated
     * voltage -- `undefined` for an unfitted joint or an actuator that comes
     * in one version. The read-back a saved configuration is written out of,
     * as [`Self::js_bus_id`] is.
     * @param {number} joint
     * @returns {number | undefined}
     */
    motorRpm(joint) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_motorRpm(retptr, this.__wbg_ptr, joint);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r2 = getDataViewMemory0().getFloat64(retptr + 8 * 1, true);
            return r0 === 0 ? undefined : r2;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_name(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * What reading the URDF had to approximate or ignore -- a link with no
     * mass, an inertia tensor that could not be used, a joint with no rotary
     * equivalent. They also go to the console, but the console is not where a
     * user looks to find out why their robot behaves oddly, so the page shows
     * them next to the robot. The rest arrive at build time, off
     * [`AddedRobot::notes`].
     * @returns {string[]}
     */
    get notes() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_notes(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The pins on the chosen microcontroller this wire may go to -- the ones
     * carrying what it is looking for. Empty until a microcontroller is chosen.
     * @param {number} joint
     * @param {number} wire
     * @returns {string[]}
     */
    pinOptions(joint, wire) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_pinOptions(retptr, this.__wbg_ptr, joint, wire);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
            wasm.__wbindgen_export4(r0, r1 * 4, 4);
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * What the robot runs on, as [`powerOptions`] names it.
     * @returns {string}
     */
    get power() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_power(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The rail the power source puts out, as the wiring step offers it.
     * @returns {string}
     */
    get powerRail() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_powerRail(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The PSRAM setting sketches for this robot are compiled with, as
     * [`psramOptions`] names it -- `undefined` until a board that offers one
     * is chosen.
     * @returns {string | undefined}
     */
    get psram() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_psram(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Take a part off the board. Everything after it moves up a place.
     * @param {number} component
     */
    removeComponent(component) {
        wasm.robotplan_removeComponent(this.__wbg_ptr, component);
    }
    /**
     * What that actuator's horn reads with this joint at zero, in degrees of
     * its own travel -- as it was stored, i.e. already clamped to that travel.
     *
     * Named for the pose it is read in, since a bare "servo degrees" is as
     * easily taken for the travel the servo has as for where it is standing
     * within it.
     *
     * `undefined` for an actuator with no travel to read a horn within -- a
     * gearmotor -- on the same terms as [`Self::js_bus_id`] for one with no
     * address: whatever angle `setActuator` was handed for it is stored and
     * never read, so there is nothing here to report or save.
     * @param {number} joint
     * @returns {number | undefined}
     */
    servoDegreesAtZeroPose(joint) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_servoDegreesAtZeroPose(retptr, this.__wbg_ptr, joint);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r2 = getDataViewMemory0().getFloat64(retptr + 8 * 1, true);
            return r0 === 0 ? undefined : r2;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Fit `actuator` to a joint, reporting what its horn reads in the robot's
     * zero pose, in degrees of the actuator's own travel.
     *
     * That reading is a calibration, not a pose: the joint is built at the
     * URDF's zero either way, and this says what the horn reads there. So it
     * is clamped to the actuator's **whole travel** -- 0 deg to 180 deg on a
     * servo -- and never to the joint's URDF limit. A horn cannot be reading
     * 200 deg, but it can perfectly well be sitting anywhere in its own travel
     * on a joint whose limit covers only part of it.
     *
     * The actuator arrives unwired and is given the first pins going, which the
     * user is then free to move.
     * @param {number} joint
     * @param {string} actuator
     * @param {number} degrees
     */
    setActuator(joint, actuator, degrees) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(actuator, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_setActuator(retptr, this.__wbg_ptr, joint, ptr0, len0, degrees);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Set the address the servo on a joint answers to.
     *
     * Separate from [`Self::js_set_actuator`] rather than another argument to
     * it, because it is a different kind of answer: fitting an actuator starts
     * that joint's wiring over from the defaults, and re-numbering a servo
     * must not. Two servos left on the same address are allowed here and
     * reported by [`Self::js_wiring_problem`], on the same terms as two of
     * them on one pin.
     * @param {number} joint
     * @param {number} id
     */
    setBusId(joint, id) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_setBusId(retptr, this.__wbg_ptr, joint, id);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Fit an image sensor to a camera link.
     *
     * Nothing else follows from it: a camera is not built into the robot, does
     * not change the joint it hangs off, and is not wired to the board. What
     * it decides is what the view through that link shows.
     * @param {number} camera
     * @param {string} sensor
     */
    setCamera(camera, sensor) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(sensor, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_setCamera(retptr, this.__wbg_ptr, camera, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Rename it. Only a label: it names the button that presses it and the
     * warning that mentions it, and nothing looks it up by name.
     * @param {number} component
     * @param {string} name
     */
    setComponentName(component, name) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.robotplan_setComponentName(this.__wbg_ptr, component, ptr0, len0);
    }
    /**
     * Connect one wire of a part to a pin, on the same terms as
     * [`Self::js_set_wire`]: it has to exist and carry what the wire wants,
     * and a clash with another part is left to [`Self::js_wiring_problem`].
     * @param {number} component
     * @param {number} wire
     * @param {string} pin
     */
    setComponentWire(component, wire, pin) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(pin, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_setComponentWire(retptr, this.__wbg_ptr, component, wire, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Rename it. Only a label, as a component's name is.
     * @param {number} driver
     * @param {string} name
     */
    setDriverName(driver, name) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.robotplan_setDriverName(this.__wbg_ptr, driver, ptr0, len0);
    }
    /**
     * Connect one wire of a driver to a pin, on the same terms as
     * [`Self::js_set_wire`].
     * @param {number} driver
     * @param {number} wire
     * @param {string} pin
     */
    setDriverWire(driver, wire, pin) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(pin, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_setDriverWire(retptr, this.__wbg_ptr, driver, wire, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Leave a joint free: no actuator on it, on purpose. It is built with no
     * armature and no friction, nothing drives it, and it swings with whatever
     * it is attached to -- a caster, a passive linkage, a pendulum.
     * @param {number} joint
     */
    setFree(joint) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_setFree(retptr, this.__wbg_ptr, joint);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Choose the microcontroller the robot runs on.
     *
     * Changing boards re-does the wiring: a pin the new board does not have
     * cannot stand, and the rest are picked again from what it does have.
     * The PSRAM setting starts over at the board's default the same way, so it
     * is set after this rather than before.
     * @param {string} microcontroller
     */
    setMicrocontroller(microcontroller) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(microcontroller, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_setMicrocontroller(retptr, this.__wbg_ptr, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Put the motor on a joint on one channel of a driver already on the
     * board.
     *
     * Refused for a channel another motor is on, which is what keeps a driver
     * to as many motors as it has channels. Moving to the other channel of the
     * driver the joint is already on is allowed, and leaves that driver where
     * it is in the list; leaving a driver empty removes it, and every driver
     * after it moves up a place.
     * @param {number} joint
     * @param {number} driver
     * @param {number} channel
     */
    setMotorDriver(joint, driver, channel) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_setMotorDriver(retptr, this.__wbg_ptr, joint, driver, channel);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Choose the gearmotor on a joint by the speed it is sold at, from
     * [`actuatorRpmOptions`].
     *
     * Separate from [`Self::js_set_actuator`] for the reason the bus address
     * is: a different gearbox is the same wires in the same pins, and must
     * not start the joint's wiring over.
     * @param {number} joint
     * @param {number} rpm
     */
    setMotorRpm(joint, rpm) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_setMotorRpm(retptr, this.__wbg_ptr, joint, rpm);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Put the motor on a joint behind a new driver of the named kind, on its
     * first channel, returning where the driver landed in the list.
     *
     * The only way a driver comes into being, which is what keeps every
     * driver on the board driving something. Whatever channel the joint was on
     * before is let go, and a driver that leaves empty goes with it -- so the
     * index handed back is the one to use, not the count before the call.
     *
     * Taken whether or not the joint has been fitted with a motor yet: the
     * panel asks for the driver in the same step as the actuator, and only
     * writes the actuator when the step is left.
     * @param {number} joint
     * @param {string} driver
     * @returns {number}
     */
    setNewMotorDriver(joint, driver) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(driver, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_setNewMotorDriver(retptr, this.__wbg_ptr, joint, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            var r2 = getDataViewMemory0().getInt32(retptr + 4 * 2, true);
            if (r2) {
                throw takeObject(r1);
            }
            return r0 >>> 0;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Choose what the robot runs on, from [`powerOptions`]. Wires on the old
     * source's rail that the new one cannot feed are put on a pin that can.
     * @param {string} power
     */
    setPower(power) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(power, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_setPower(retptr, this.__wbg_ptr, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Choose the PSRAM setting, from those the chosen board offers.
     * @param {string} psram
     */
    setPsram(psram) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(psram, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_setPsram(retptr, this.__wbg_ptr, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Choose the step-down converter, from [`stepDownOptions`].
     * @param {string} step_down
     */
    setStepDown(step_down) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(step_down, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_setStepDown(retptr, this.__wbg_ptr, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Connect one wire to a pin.
     *
     * The pin has to exist on the chosen board and carry what the wire is
     * looking for; two servos sharing a signal pin is allowed here and reported
     * by [`Self::js_wiring_problem`], so a half-finished swap is not rejected
     * mid-way.
     * @param {number} joint
     * @param {number} wire
     * @param {string} pin
     */
    setWire(joint, wire, pin) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            const ptr0 = passStringToWasm0(pin, wasm.__wbindgen_export, wasm.__wbindgen_export2);
            const len0 = WASM_VECTOR_LEN;
            wasm.robotplan_setWire(retptr, this.__wbg_ptr, joint, wire, ptr0, len0);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            if (r1) {
                throw takeObject(r0);
            }
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The step-down converter fitted, as [`stepDownOptions`] names it.
     * @returns {string}
     */
    get stepDown() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_stepDown(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * What the fitted step-down converter puts out, in volts.
     * @returns {number}
     */
    get stepDownOutputVolts() {
        const ret = wasm.robotplan_stepDownOutputVolts(this.__wbg_ptr);
        return ret;
    }
    /**
     * The rail the step-down converter puts out, as the wiring step offers it.
     * @returns {string}
     */
    get stepDownRail() {
        let deferred1_0;
        let deferred1_1;
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_stepDownRail(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            deferred1_0 = r0;
            deferred1_1 = r1;
            return getStringFromWasm0(r0, r1);
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
            wasm.__wbindgen_export4(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * How many wires the actuator on this joint has, 0 if it has no actuator.
     * @param {number} joint
     * @returns {number}
     */
    wireCount(joint) {
        const ret = wasm.robotplan_wireCount(this.__wbg_ptr, joint);
        return ret >>> 0;
    }
    /**
     * The name of one wire, as the servo's lead is coloured.
     * @param {number} joint
     * @param {number} wire
     * @returns {string | undefined}
     */
    wireName(joint, wire) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_wireName(retptr, this.__wbg_ptr, joint, wire);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The pin this wire is currently connected to.
     * @param {number} joint
     * @param {number} wire
     * @returns {string | undefined}
     */
    wirePin(joint, wire) {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_wirePin(retptr, this.__wbg_ptr, joint, wire);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Something about the wiring as it stands that is wrong on the real board
     * and harmless here -- today, an N20's encoder powered from a rail above
     * the board's own logic level. The first found, as with a problem.
     *
     * The other half of [`Self::js_wiring_note`], split from it because this
     * one moves with the pins: the note is settled in the hardware step and
     * shown once, while this is asked again on every pin change, beside
     * [`Self::js_wiring_problem`]. Not a problem, because nothing here stops
     * working -- the emulated GPIO reads A and B as digital levels, and a
     * 5 V high is a high -- but on a real S3 it is 5 V into a 3.3 V pad, which
     * is the kind of thing a simulator ought to say before a soldering iron
     * does. [`Self::js_add_to`] adds it to the build notes as well, for the
     * same reason the note goes there.
     * @returns {string | undefined}
     */
    wiringCaution() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_wiringCaution(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * Something about the wiring worth knowing that does not stop the robot
     * being added -- today, only that its actuators want more hardware PWM
     * channels than the board has.
     *
     * Not a [`Self::js_wiring_problem`], because a sketch can get round it: the
     * joints past the board's LEDC channels move if the sketch drives their
     * pins with a software PWM, which the emulated GPIO reads like any other
     * output. What it cannot know is whether the sketch will, so it says what
     * happens if it does not. The panel shows it under the wiring, and
     * [`Self::js_add_to`] adds it to the build notes, since a saved
     * configuration that applies cleanly never opens the panel.
     *
     * The count is the S3's eight LEDC channels, which a pulse servo takes
     * one of and a motor bridge two. A bus servo takes none -- twenty of them
     * are one serial port and one wire, which is a large part of why anyone
     * fits them.
     * @returns {string | undefined}
     */
    wiringNote() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_wiringNote(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
    /**
     * The first thing wrong with the wiring as it stands, if anything is.
     *
     * One message rather than a list: the UI shows it under the wiring and
     * keeps "add" disabled until it comes back empty, so the next problem
     * surfaces as soon as this one is fixed.
     *
     * Servos and components are checked together, since they share the board:
     * a switch read on the pin a servo is driven from is the same clash as two
     * servos on one pin, and neither would work.
     * @returns {string | undefined}
     */
    wiringProblem() {
        try {
            const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
            wasm.robotplan_wiringProblem(retptr, this.__wbg_ptr);
            var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
            var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
            let v1;
            if (r0 !== 0) {
                v1 = getStringFromWasm0(r0, r1).slice();
                wasm.__wbindgen_export4(r0, r1 * 1, 1);
            }
            return v1;
        } finally {
            wasm.__wbindgen_add_to_stack_pointer(16);
        }
    }
}
if (Symbol.dispose) RobotPlan.prototype[Symbol.dispose] = RobotPlan.prototype.free;

/**
 * The addresses the named actuator can be given on its bus, `[low, high]`.
 *
 * Empty for an actuator that is not on one, which is how the UI knows not to
 * ask: a pulse servo is addressed by the pin it listens to. Keyed by name
 * rather than by joint for the reason [`actuatorTravelDegrees`] is -- the
 * panel needs it while the user is still choosing, before anything is fitted.
 * @param {string} actuator
 * @returns {Uint8Array}
 */
export function actuatorBusIdRange(actuator) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        const ptr0 = passStringToWasm0(actuator, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.actuatorBusIdRange(retptr, ptr0, len0);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v2 = getArrayU8FromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 1, 1);
        return v2;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * The drivers the named actuator can be wired through, as [`DRIVERS`] names
 * them.
 *
 * Empty for an actuator that needs none, which is how the UI knows not to ask:
 * a servo has its own electronics, and only a motor commanded by an H-bridge
 * needs one in front of it. Keyed by name for the reason
 * [`actuatorTravelDegrees`] is.
 * @param {string} actuator
 * @returns {string[]}
 */
export function actuatorDriverOptions(actuator) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        const ptr0 = passStringToWasm0(actuator, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.actuatorDriverOptions(retptr, ptr0, len0);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v2 = getArrayJsValueFromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 4, 4);
        return v2;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * The actuators the UI can offer for a joint.
 * @returns {string[]}
 */
export function actuatorOptions() {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        wasm.actuatorOptions(retptr);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 4, 4);
        return v1;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * The speeds the named actuator is sold at, in rpm at its rated voltage,
 * slowest first.
 *
 * A gearmotor is one motor behind many gearboxes, and a shop lists them by
 * the output speed rather than the ratio -- so that is what the panel offers,
 * and the ratio follows from it. Empty for an actuator that comes in one
 * version, which is how the UI knows not to ask. Keyed by name for the reason
 * [`actuatorTravelDegrees`] is.
 * @param {string} actuator
 * @returns {Float64Array}
 */
export function actuatorRpmOptions(actuator) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        const ptr0 = passStringToWasm0(actuator, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.actuatorRpmOptions(retptr, ptr0, len0);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v2 = getArrayF64FromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 8, 8);
        return v2;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * How far the named actuator's output turns, in degrees, low end first.
 *
 * The range the UI asks the user to report the horn's position within, which
 * it needs before the actuator has been fitted to anything -- hence keyed by
 * name rather than by joint. Empty for an actuator with no horn to read: one
 * that turns continuously, like a gearmotor, for which the panel asks nothing.
 * @param {string} actuator
 * @returns {Float64Array}
 */
export function actuatorTravelDegrees(actuator) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        const ptr0 = passStringToWasm0(actuator, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.actuatorTravelDegrees(retptr, ptr0, len0);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v2 = getArrayF64FromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 8, 8);
        return v2;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * Read the mesh in a dropped folder, put it into a scene that is already
 * running, and hand back the handle the page places it with.
 *
 * Takes the folder by value, as [`crate::plan::planRobotFromFolder`] does, and
 * for the simpler reason: the mesh is read here and there is nothing left to
 * come back for.
 *
 * The object arrives made of the first material in the table and standing over
 * the origin with its lowest point just clear of the ground -- a starting point
 * for the placement rather than an opinion about where it belongs. The caller
 * is expected to have stopped the simulation before this and to start it again
 * once the user has placed it; nothing here does that, since the freeze has to
 * cover the panel as well.
 * @param {DroppedFolder} folder
 * @param {InterfaceHybrid} hybrid
 * @returns {AddedObject}
 */
export function addObjectFromFolder(folder, hybrid) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        _assertClass(folder, DroppedFolder);
        var ptr0 = folder.__destroy_into_raw();
        _assertClass(hybrid, InterfaceHybrid);
        wasm.addObjectFromFolder(retptr, ptr0, hybrid.__wbg_ptr);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var r2 = getDataViewMemory0().getInt32(retptr + 4 * 2, true);
        if (r2) {
            throw takeObject(r1);
        }
        return AddedObject.__wrap(r0);
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * The image sensors the UI can offer for a camera link.
 * @returns {string[]}
 */
export function cameraOptions() {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        wasm.cameraOptions(retptr);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 4, 4);
        return v1;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * The parts the UI can offer to add to the board, as opposed to a joint.
 * @returns {string[]}
 */
export function componentOptions() {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        wasm.componentOptions(retptr);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 4, 4);
        return v1;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * @param {number} w_body
 * @param {number} h_body
 * @param {number} r_leg
 * @param {number} r_foot
 * @param {number} body_leg_length
 * @param {number} leg_foot_length
 * @returns {InterfaceMechanismState}
 */
export function create1DHopper(w_body, h_body, r_leg, r_foot, body_leg_length, leg_foot_length) {
    const ret = wasm.create1DHopper(w_body, h_body, r_leg, r_foot, body_leg_length, leg_foot_length);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @param {number} w_body
 * @param {number} h_body
 * @param {number} r_hip
 * @param {number} body_hip_length
 * @param {number} r_piston
 * @param {number} hip_piston_length
 * @param {number} l_leg
 * @param {number} piston_leg_length
 * @returns {InterfaceMechanismState}
 */
export function create2DHopper(w_body, h_body, r_hip, body_hip_length, r_piston, hip_piston_length, l_leg, piston_leg_length) {
    const ret = wasm.create2DHopper(w_body, h_body, r_hip, body_hip_length, r_piston, hip_piston_length, l_leg, piston_leg_length);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @returns {InterfaceMechanismState}
 */
export function createActuatedAnkleHopper() {
    const ret = wasm.createActuatedAnkleHopper();
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @returns {InterfaceMechanismState}
 */
export function createActuatedHipHopper() {
    const ret = wasm.createActuatedHipHopper();
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createBalancingBot() {
    const ret = wasm.createBalancingBot();
    return takeObject(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createBalancingBotController() {
    const ret = wasm.createBalancingBotController();
    return InterfaceController.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createBiped() {
    const ret = wasm.createBiped();
    return takeObject(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createBipedController() {
    const ret = wasm.createBipedController();
    return InterfaceController.__wrap(ret);
}

/**
 * @param {number} length
 * @returns {InterfaceMechanismState}
 */
export function createCart(length) {
    const ret = wasm.createCart(length);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @param {number} length
 * @returns {InterfaceMechanismState}
 */
export function createCartPole(length) {
    const ret = wasm.createCartPole(length);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceCloth>}
 */
export function createCloth() {
    const ret = wasm.createCloth();
    return takeObject(ret);
}

/**
 * @returns {InterfaceMechanismState}
 */
export function createCompassGait() {
    const ret = wasm.createCompassGait();
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @param {number} length
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createCube(length) {
    const ret = wasm.createCube(length);
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createCubeFrenzy() {
    const ret = wasm.createCubeFrenzy();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createCuboidCart() {
    const ret = wasm.createCuboidCart();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createDesktop() {
    const ret = wasm.createDesktop();
    return takeObject(ret);
}

/**
 * @param {number} length
 * @returns {InterfaceMechanismState}
 */
export function createDoublePendulum(length) {
    const ret = wasm.createDoublePendulum(length);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createDoublePendulumAndCube() {
    const ret = wasm.createDoublePendulumAndCube();
    return takeObject(ret);
}

/**
 * @param {number} length
 * @returns {InterfaceMechanismState}
 */
export function createDoublePendulumHorizontal(length) {
    const ret = wasm.createDoublePendulumHorizontal(length);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createEnv() {
    const ret = wasm.createEnv();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceFEMDeformable>}
 */
export function createFEMBox() {
    const ret = wasm.createFEMBox();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceFluid2D>}
 */
export function createFluid2D() {
    const ret = wasm.createFluid2D();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createFourBarLinkage() {
    const ret = wasm.createFourBarLinkage();
    return takeObject(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createFourBarLinkageController() {
    const ret = wasm.createFourBarLinkageController();
    return InterfaceController.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createFourBarLinkageWithBase() {
    const ret = wasm.createFourBarLinkageWithBase();
    return takeObject(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createFourBarLinkageWithBaseController() {
    const ret = wasm.createFourBarLinkageWithBaseController();
    return InterfaceController.__wrap(ret);
}

/**
 * @returns {InterfaceMechanismState}
 */
export function createGripper() {
    const ret = wasm.createGripper();
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createGripperHybrid() {
    const ret = wasm.createGripperHybrid();
    return takeObject(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createGripperManualController() {
    const ret = wasm.createGripperManualController();
    return InterfaceController.__wrap(ret);
}

/**
 * @param {number} h_setpoint
 * @param {number} body_leg_length
 * @param {number} leg_foot_length
 * @returns {InterfaceController}
 */
export function createHopper1DController(h_setpoint, body_leg_length, leg_foot_length) {
    const ret = wasm.createHopper1DController(h_setpoint, body_leg_length, leg_foot_length);
    return InterfaceController.__wrap(ret);
}

/**
 * @param {number} h_setpoint
 * @param {number} body_hip_length
 * @param {number} hip_leg_length
 * @param {number} leg_foot_length
 * @param {number} l_leg
 * @param {number} k_spring
 * @returns {InterfaceController}
 */
export function createHopper2DController(h_setpoint, body_hip_length, hip_leg_length, leg_foot_length, l_leg, k_spring) {
    const ret = wasm.createHopper2DController(h_setpoint, body_hip_length, hip_leg_length, leg_foot_length, l_leg, k_spring);
    return InterfaceController.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createHybridCube() {
    const ret = wasm.createHybridCube();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createHybridSphereAndTetra() {
    const ret = wasm.createHybridSphereAndTetra();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createLeg() {
    const ret = wasm.createLeg();
    return takeObject(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createLegController() {
    const ret = wasm.createLegController();
    return InterfaceController.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createLegFromFoot() {
    const ret = wasm.createLegFromFoot();
    return takeObject(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createLegFromFootController() {
    const ret = wasm.createLegFromFootController();
    return InterfaceController.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceMPMDeformable>}
 */
export function createMPMDeformable() {
    const ret = wasm.createMPMDeformable();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceMassSpringDeformable>}
 */
export function createMassSpringBunny() {
    const ret = wasm.createMassSpringBunny();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceMassSpring>}
 */
export function createMassSpringTetrahedron() {
    const ret = wasm.createMassSpringTetrahedron();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createMockNavbot() {
    const ret = wasm.createMockNavbot();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createNavbot() {
    const ret = wasm.createNavbot();
    return takeObject(ret);
}

/**
 * @param {number} dt
 * @returns {InterfaceController}
 */
export function createNavbotController(dt) {
    const ret = wasm.createNavbotController(dt);
    return InterfaceController.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createNavbotMotor() {
    const ret = wasm.createNavbotMotor();
    return takeObject(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createNullController() {
    const ret = wasm.createNullController();
    return InterfaceController.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createParallelBar() {
    const ret = wasm.createParallelBar();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createPendulum() {
    const ret = wasm.createPendulum();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createPusher() {
    const ret = wasm.createPusher();
    return takeObject(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createPusherController() {
    const ret = wasm.createPusherController();
    return InterfaceController.__wrap(ret);
}

/**
 * @param {number} l_leg
 * @param {number} default_z
 * @returns {InterfaceMechanismState}
 */
export function createQuadruped(l_leg, default_z) {
    const ret = wasm.createQuadruped(l_leg, default_z);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createQuadrupedStandingController() {
    const ret = wasm.createQuadrupedStandingController();
    return InterfaceController.__wrap(ret);
}

/**
 * @param {number} dt
 * @param {number} target_x
 * @param {number} default_foot_z
 * @returns {InterfaceController}
 */
export function createQuadrupedTrottingController(dt, target_x, default_foot_z) {
    const ret = wasm.createQuadrupedTrottingController(dt, target_x, default_foot_z);
    return InterfaceController.__wrap(ret);
}

/**
 * Create a rigid body box from a box mesh file
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createRigidMeshBox() {
    const ret = wasm.createRigidMeshBox();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createRigidTetrahedron() {
    const ret = wasm.createRigidTetrahedron();
    return takeObject(ret);
}

/**
 * @param {number} r_body
 * @param {number} n_foot
 * @returns {InterfaceMechanismState}
 */
export function createRimlessWheel(r_body, n_foot) {
    const ret = wasm.createRimlessWheel(r_body, n_foot);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * Create a uniform rod pendulum that hangs horizontally to the right.
 * @param {number} length
 * @returns {InterfaceMechanismState}
 */
export function createRodPendulum(length) {
    const ret = wasm.createRodPendulum(length);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @param {number} length
 * @returns {InterfaceMechanismState}
 */
export function createRodPendulumAtBottom(length) {
    const ret = wasm.createRodPendulumAtBottom(length);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createSO101() {
    const ret = wasm.createSO101();
    return takeObject(ret);
}

/**
 * @returns {InterfaceController}
 */
export function createSO101PositionController() {
    const ret = wasm.createSO101PositionController();
    return InterfaceController.__wrap(ret);
}

/**
 * Create a simple pendulum with a contact point at the end of the rod
 * @param {number} mass
 * @param {number} length
 * @returns {InterfaceMechanismState}
 */
export function createSimplePendulum(mass, length) {
    const ret = wasm.createSimplePendulum(mass, length);
    return InterfaceMechanismState.__wrap(ret);
}

/**
 * @param {number} mass
 * @param {number} radius
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createSphere(mass, radius) {
    const ret = wasm.createSphere(mass, radius);
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createSphereCart() {
    const ret = wasm.createSphereCart();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceHybrid>}
 */
export function createTeddy() {
    const ret = wasm.createTeddy();
    return takeObject(ret);
}

/**
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createTwoRigidCubes() {
    const ret = wasm.createTwoRigidCubes();
    return takeObject(ret);
}

/**
 * Create two rigid body boxes from a box mesh file
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createTwoRigidMeshBoxes() {
    const ret = wasm.createTwoRigidMeshBoxes();
    return takeObject(ret);
}

/**
 * Create two rigid body tetrahedrons from a tetrahedron mesh file
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createTwoRigidTetrahedron() {
    const ret = wasm.createTwoRigidTetrahedron();
    return takeObject(ret);
}

/**
 * @param {number} mass
 * @param {number} radius
 * @returns {Promise<InterfaceMechanismState>}
 */
export function createTwoSphere(mass, radius) {
    const ret = wasm.createTwoSphere(mass, radius);
    return takeObject(ret);
}

/**
 * What one of them weighs per cubic metre, so the panel can show the density
 * behind a named material without keeping its own copy of the table.
 * @param {string} name
 * @returns {number | undefined}
 */
export function materialDensity(name) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.materialDensity(retptr, ptr0, len0);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r2 = getDataViewMemory0().getFloat64(retptr + 8 * 1, true);
        return r0 === 0 ? undefined : r2;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * And what it slides on the ground at, for the same reason.
 * @param {string} name
 * @returns {number | undefined}
 */
export function materialFriction(name) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.materialFriction(retptr, ptr0, len0);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r2 = getDataViewMemory0().getFloat64(retptr + 8 * 1, true);
        return r0 === 0 ? undefined : r2;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * The materials the UI can offer, in table order -- the first is the default an
 * object is built with.
 * @returns {string[]}
 */
export function materialOptions() {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        wasm.materialOptions(retptr);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 4, 4);
        return v1;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * The microcontrollers the UI can offer.
 * @returns {string[]}
 */
export function microcontrollerOptions() {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        wasm.microcontrollerOptions(retptr);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 4, 4);
        return v1;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * Read the robot in a dropped folder, ready to be configured.
 *
 * Takes the folder rather than borrowing it: the plan holds on to it for as
 * long as the robot it describes is in the scene -- the meshes are re-read
 * every time it is built -- so JS must not touch the folder afterwards, there
 * being nothing left on that handle to free.
 * @param {DroppedFolder} folder
 * @returns {RobotPlan}
 */
export function planRobotFromFolder(folder) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        _assertClass(folder, DroppedFolder);
        var ptr0 = folder.__destroy_into_raw();
        wasm.planRobotFromFolder(retptr, ptr0);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var r2 = getDataViewMemory0().getInt32(retptr + 4 * 2, true);
        if (r2) {
            throw takeObject(r1);
        }
        return RobotPlan.__wrap(r0);
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * The power sources a robot can run on, the default first.
 * @returns {string[]}
 */
export function powerOptions() {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        wasm.powerOptions(retptr);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 4, 4);
        return v1;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * The PSRAM settings the named board can be compiled with, the default first.
 *
 * Empty for a board with no such choice, which is how the UI knows not to ask.
 * Keyed by name for the reason [`actuatorTravelDegrees`] is: the panel needs it
 * while the board is still being chosen, before the plan has been told.
 * @param {string} microcontroller
 * @returns {string[]}
 */
export function psramOptions(microcontroller) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        const ptr0 = passStringToWasm0(microcontroller, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.psramOptions(retptr, ptr0, len0);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v2 = getArrayJsValueFromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 4, 4);
        return v2;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * Turn the element with id `element_id` into a folder drop zone.
 *
 * Every file under a dropped folder is read into wasm, the result is inspected
 * (see [`inspect::report`]) and logged, and `on_folder` -- if given -- is called
 * with the [`DroppedFolder`] once it is fully read.
 *
 * `on_reading` -- if given -- is called with `true` the moment a drop starts
 * and with `false` when the read is over, whichever way it went. That span is
 * the one part of a drop the caller cannot see: it is a file at a time with an
 * `await` between, so the browser is free to run the page's animation frames
 * in the gaps, and on a page whose run loop is simulating a robot there is
 * very little left over. The caller is expected to stop the simulation for the
 * duration, which is what makes replacing a robot cost what the first one did.
 * @param {string} element_id
 * @param {Function | null} [on_folder]
 * @param {Function | null} [on_reading]
 */
export function registerFolderDrop(element_id, on_folder, on_reading) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        const ptr0 = passStringToWasm0(element_id, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.registerFolderDrop(retptr, ptr0, len0, isLikeNone(on_folder) ? 0 : addHeapObject(on_folder), isLikeNone(on_reading) ? 0 : addHeapObject(on_reading));
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        if (r1) {
            throw takeObject(r0);
        }
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * Install the panic hook at module init so panics surface as readable console
 * errors with a stack, rather than a bare `unreachable`.
 *
 * gorilla-physics already calls `set_once()` inside `InterfaceHybrid::new`, but
 * that leaves anything running before it (e.g. `Hybrid::empty()`) uncovered.
 * `set_once` is idempotent, so the two do not conflict.
 */
export function start() {
    wasm.start();
}

/**
 * The step-down converters a robot can be fitted with, the default first.
 * @returns {string[]}
 */
export function stepDownOptions() {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        wasm.stepDownOptions(retptr);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        var v1 = getArrayJsValueFromWasm0(r0, r1).slice();
        wasm.__wbindgen_export4(r0, r1 * 4, 4);
        return v1;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

/**
 * What the named step-down converter puts out, in volts -- `undefined` for a
 * name not on offer. Keyed by name so the panel can label each option.
 * @param {string} name
 * @returns {number | undefined}
 */
export function stepDownOutputVolts(name) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_export, wasm.__wbindgen_export2);
        const len0 = WASM_VECTOR_LEN;
        wasm.stepDownOutputVolts(retptr, ptr0, len0);
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r2 = getDataViewMemory0().getFloat64(retptr + 8 * 1, true);
        return r0 === 0 ? undefined : r2;
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}
export function __wbg___wbindgen_debug_string_c25d447a39f5578f(arg0, arg1) {
    const ret = debugString(getObject(arg1));
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_export, wasm.__wbindgen_export2);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg___wbindgen_is_function_1ff95bcc5517c252(arg0) {
    const ret = typeof(getObject(arg0)) === 'function';
    return ret;
}
export function __wbg___wbindgen_is_object_a27215656b807791(arg0) {
    const val = getObject(arg0);
    const ret = typeof(val) === 'object' && val !== null;
    return ret;
}
export function __wbg___wbindgen_is_string_ea5e6cc2e4141dfe(arg0) {
    const ret = typeof(getObject(arg0)) === 'string';
    return ret;
}
export function __wbg___wbindgen_is_undefined_c05833b95a3cf397(arg0) {
    const ret = getObject(arg0) === undefined;
    return ret;
}
export function __wbg___wbindgen_string_get_b0ca35b86a603356(arg0, arg1) {
    const obj = getObject(arg1);
    const ret = typeof(obj) === 'string' ? obj : undefined;
    var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_export, wasm.__wbindgen_export2);
    var len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg___wbindgen_throw_344f42d3211c4765(arg0, arg1) {
    throw new Error(getStringFromWasm0(arg0, arg1));
}
export function __wbg__wbg_cb_unref_fffb441def202758(arg0) {
    getObject(arg0)._wbg_cb_unref();
}
export function __wbg_addEventListener_d85450ee1320c989() { return handleError(function (arg0, arg1, arg2, arg3) {
    getObject(arg0).addEventListener(getStringFromWasm0(arg1, arg2), getObject(arg3));
}, arguments); }
export function __wbg_arrayBuffer_a158e423a87ee756(arg0) {
    const ret = getObject(arg0).arrayBuffer();
    return addHeapObject(ret);
}
export function __wbg_call_a6e5c5dce5018821() { return handleError(function (arg0, arg1, arg2) {
    const ret = getObject(arg0).call(getObject(arg1), getObject(arg2));
    return addHeapObject(ret);
}, arguments); }
export function __wbg_createReader_ecea30849b67dde0(arg0) {
    const ret = getObject(arg0).createReader();
    return addHeapObject(ret);
}
export function __wbg_crypto_38df2bab126b63dc(arg0) {
    const ret = getObject(arg0).crypto;
    return addHeapObject(ret);
}
export function __wbg_dataTransfer_c1c4745cee7e05f1(arg0) {
    const ret = getObject(arg0).dataTransfer;
    return isLikeNone(ret) ? 0 : addHeapObject(ret);
}
export function __wbg_document_179650d6cb13c263(arg0) {
    const ret = getObject(arg0).document;
    return isLikeNone(ret) ? 0 : addHeapObject(ret);
}
export function __wbg_droppedfolder_new(arg0) {
    const ret = DroppedFolder.__wrap(arg0);
    return addHeapObject(ret);
}
export function __wbg_error_744744ff0c9861e6(arg0) {
    console.error(getObject(arg0));
}
export function __wbg_error_7ed559cd7146b49d(arg0, arg1) {
    console.error(getObject(arg0), getObject(arg1));
}
export function __wbg_error_a6fa202b58aa1cd3(arg0, arg1) {
    let deferred0_0;
    let deferred0_1;
    try {
        deferred0_0 = arg0;
        deferred0_1 = arg1;
        console.error(getStringFromWasm0(arg0, arg1));
    } finally {
        wasm.__wbindgen_export4(deferred0_0, deferred0_1, 1);
    }
}
export function __wbg_fetch_58167e2c1ef32e0a(arg0, arg1, arg2) {
    const ret = getObject(arg0).fetch(getStringFromWasm0(arg1, arg2));
    return addHeapObject(ret);
}
export function __wbg_file_828cf42438c6dfcd(arg0, arg1, arg2) {
    getObject(arg0).file(getObject(arg1), getObject(arg2));
}
export function __wbg_fullPath_5159e6a3a5cbff25(arg0, arg1) {
    const ret = getObject(arg1).fullPath;
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_export, wasm.__wbindgen_export2);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_getElementById_1cbd8f06dbe8eb8e(arg0, arg1, arg2) {
    const ret = getObject(arg0).getElementById(getStringFromWasm0(arg1, arg2));
    return isLikeNone(ret) ? 0 : addHeapObject(ret);
}
export function __wbg_getRandomValues_3f44b700395062e5() { return handleError(function (arg0, arg1) {
    globalThis.crypto.getRandomValues(getArrayU8FromWasm0(arg0, arg1));
}, arguments); }
export function __wbg_getRandomValues_c44a50d8cfdaebeb() { return handleError(function (arg0, arg1) {
    getObject(arg0).getRandomValues(getObject(arg1));
}, arguments); }
export function __wbg_get_e73985d6689d2245(arg0, arg1) {
    const ret = getObject(arg0)[arg1 >>> 0];
    return isLikeNone(ret) ? 0 : addHeapObject(ret);
}
export function __wbg_get_unchecked_6e0ad6d2a41b06f6(arg0, arg1) {
    const ret = getObject(arg0)[arg1 >>> 0];
    return addHeapObject(ret);
}
export function __wbg_instanceof_Response_c8b64b2256f01bec(arg0) {
    let result;
    try {
        result = getObject(arg0) instanceof Response;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Window_05ba1ee4f6781663(arg0) {
    let result;
    try {
        result = getObject(arg0) instanceof Window;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_interfacecloth_new(arg0) {
    const ret = InterfaceCloth.__wrap(arg0);
    return addHeapObject(ret);
}
export function __wbg_interfacefemdeformable_new(arg0) {
    const ret = InterfaceFEMDeformable.__wrap(arg0);
    return addHeapObject(ret);
}
export function __wbg_interfacefluid2d_new(arg0) {
    const ret = InterfaceFluid2D.__wrap(arg0);
    return addHeapObject(ret);
}
export function __wbg_interfacehybrid_new(arg0) {
    const ret = InterfaceHybrid.__wrap(arg0);
    return addHeapObject(ret);
}
export function __wbg_interfacemassspring_new(arg0) {
    const ret = InterfaceMassSpring.__wrap(arg0);
    return addHeapObject(ret);
}
export function __wbg_interfacemassspringdeformable_new(arg0) {
    const ret = InterfaceMassSpringDeformable.__wrap(arg0);
    return addHeapObject(ret);
}
export function __wbg_interfacemechanismstate_new(arg0) {
    const ret = InterfaceMechanismState.__wrap(arg0);
    return addHeapObject(ret);
}
export function __wbg_interfacempmdeformable_new(arg0) {
    const ret = InterfaceMPMDeformable.__wrap(arg0);
    return addHeapObject(ret);
}
export function __wbg_isDirectory_9ebbbf40afa78193(arg0) {
    const ret = getObject(arg0).isDirectory;
    return ret;
}
export function __wbg_isFile_8e4145e0d9d18b14(arg0) {
    const ret = getObject(arg0).isFile;
    return ret;
}
export function __wbg_items_350b6f2d566d3def(arg0) {
    const ret = getObject(arg0).items;
    return addHeapObject(ret);
}
export function __wbg_kind_c494ca014c671a6f(arg0, arg1) {
    const ret = getObject(arg1).kind;
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_export, wasm.__wbindgen_export2);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_length_1f0964f4a5e2c6d8(arg0) {
    const ret = getObject(arg0).length;
    return ret;
}
export function __wbg_length_370319915dc99107(arg0) {
    const ret = getObject(arg0).length;
    return ret;
}
export function __wbg_length_e08fc23135c66d6f(arg0) {
    const ret = getObject(arg0).length;
    return ret;
}
export function __wbg_log_d267660666346fb3(arg0) {
    console.log(getObject(arg0));
}
export function __wbg_msCrypto_bd5a034af96bcba6(arg0) {
    const ret = getObject(arg0).msCrypto;
    return addHeapObject(ret);
}
export function __wbg_name_4819ebbff233a2f1(arg0, arg1) {
    const ret = getObject(arg1).name;
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_export, wasm.__wbindgen_export2);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_new_227d7c05414eb861() {
    const ret = new Error();
    return addHeapObject(ret);
}
export function __wbg_new_aec3e25493d729fe(arg0, arg1) {
    try {
        var state0 = {a: arg0, b: arg1};
        var cb0 = (arg0, arg1) => {
            const a = state0.a;
            state0.a = 0;
            try {
                return __wasm_bindgen_func_elem_4389(a, state0.b, arg0, arg1);
            } finally {
                state0.a = a;
            }
        };
        const ret = new Promise(cb0);
        return addHeapObject(ret);
    } finally {
        state0.a = 0;
    }
}
export function __wbg_new_cd45aabdf6073e84(arg0) {
    const ret = new Uint8Array(getObject(arg0));
    return addHeapObject(ret);
}
export function __wbg_new_from_slice_7568ba55b4a7e81f(arg0, arg1) {
    const ret = new Uint32Array(getArrayU32FromWasm0(arg0, arg1));
    return addHeapObject(ret);
}
export function __wbg_new_from_slice_77cdfb7977362f3c(arg0, arg1) {
    const ret = new Uint8Array(getArrayU8FromWasm0(arg0, arg1));
    return addHeapObject(ret);
}
export function __wbg_new_from_slice_7e254b47c77fb8cc(arg0, arg1) {
    const ret = new Float64Array(getArrayF64FromWasm0(arg0, arg1));
    return addHeapObject(ret);
}
export function __wbg_new_from_slice_ddf8b82c4d6af38e(arg0, arg1) {
    const ret = new Float32Array(getArrayF32FromWasm0(arg0, arg1));
    return addHeapObject(ret);
}
export function __wbg_new_typed_1824d93f294193e5(arg0, arg1) {
    try {
        var state0 = {a: arg0, b: arg1};
        var cb0 = (arg0, arg1) => {
            const a = state0.a;
            state0.a = 0;
            try {
                return __wasm_bindgen_func_elem_4389(a, state0.b, arg0, arg1);
            } finally {
                state0.a = a;
            }
        };
        const ret = new Promise(cb0);
        return addHeapObject(ret);
    } finally {
        state0.a = 0;
    }
}
export function __wbg_new_with_length_112583d83d0cb73c(arg0) {
    const ret = new Float64Array(arg0 >>> 0);
    return addHeapObject(ret);
}
export function __wbg_new_with_length_e6785c33c8e4cce8(arg0) {
    const ret = new Uint8Array(arg0 >>> 0);
    return addHeapObject(ret);
}
export function __wbg_node_84ea875411254db1(arg0) {
    const ret = getObject(arg0).node;
    return addHeapObject(ret);
}
export function __wbg_now_390768da5ee9e776(arg0) {
    const ret = getObject(arg0).now();
    return ret;
}
export function __wbg_now_cace042f68c814d8(arg0) {
    const ret = getObject(arg0).now();
    return ret;
}
export function __wbg_performance_3ef602e13d6c3b56(arg0) {
    const ret = getObject(arg0).performance;
    return isLikeNone(ret) ? 0 : addHeapObject(ret);
}
export function __wbg_performance_5fc5a6563dcd33de(arg0) {
    const ret = getObject(arg0).performance;
    return addHeapObject(ret);
}
export function __wbg_preventDefault_b64888c857500682(arg0) {
    getObject(arg0).preventDefault();
}
export function __wbg_process_44c7a14e11e9f69e(arg0) {
    const ret = getObject(arg0).process;
    return addHeapObject(ret);
}
export function __wbg_prototypesetcall_4770620bbe4688a0(arg0, arg1, arg2) {
    Uint8Array.prototype.set.call(getArrayU8FromWasm0(arg0, arg1), getObject(arg2));
}
export function __wbg_queueMicrotask_0ab5b2d2393e99b9(arg0) {
    const ret = getObject(arg0).queueMicrotask;
    return addHeapObject(ret);
}
export function __wbg_queueMicrotask_6a09b7bc46549209(arg0) {
    queueMicrotask(getObject(arg0));
}
export function __wbg_randomFillSync_6c25eac9869eb53c() { return handleError(function (arg0, arg1) {
    getObject(arg0).randomFillSync(takeObject(arg1));
}, arguments); }
export function __wbg_readEntries_8ef841cc9a100c0e() { return handleError(function (arg0, arg1, arg2) {
    getObject(arg0).readEntries(getObject(arg1), getObject(arg2));
}, arguments); }
export function __wbg_require_b4edbdcf3e2a1ef0() { return handleError(function () {
    const ret = module.require;
    return addHeapObject(ret);
}, arguments); }
export function __wbg_resolve_2191a4dfe481c25b(arg0) {
    const ret = Promise.resolve(getObject(arg0));
    return addHeapObject(ret);
}
export function __wbg_set_dropEffect_ee044779833a313d(arg0, arg1, arg2) {
    getObject(arg0).dropEffect = getStringFromWasm0(arg1, arg2);
}
export function __wbg_set_index_2eba9e8997f36eb4(arg0, arg1, arg2) {
    getObject(arg0)[arg1 >>> 0] = arg2;
}
export function __wbg_stack_3b0d974bbf31e44f(arg0, arg1) {
    const ret = getObject(arg1).stack;
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_export, wasm.__wbindgen_export2);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_static_accessor_GLOBAL_4ef717fb391d88b7() {
    const ret = typeof global === 'undefined' ? null : global;
    return isLikeNone(ret) ? 0 : addHeapObject(ret);
}
export function __wbg_static_accessor_GLOBAL_THIS_8d1badc68b5a74f4() {
    const ret = typeof globalThis === 'undefined' ? null : globalThis;
    return isLikeNone(ret) ? 0 : addHeapObject(ret);
}
export function __wbg_static_accessor_SELF_146583524fe1469b() {
    const ret = typeof self === 'undefined' ? null : self;
    return isLikeNone(ret) ? 0 : addHeapObject(ret);
}
export function __wbg_static_accessor_WINDOW_f2829a2234d7819e() {
    const ret = typeof window === 'undefined' ? null : window;
    return isLikeNone(ret) ? 0 : addHeapObject(ret);
}
export function __wbg_subarray_3ed232c8a6baee09(arg0, arg1, arg2) {
    const ret = getObject(arg0).subarray(arg1 >>> 0, arg2 >>> 0);
    return addHeapObject(ret);
}
export function __wbg_text_d3a29f7525a132c3() { return handleError(function (arg0) {
    const ret = getObject(arg0).text();
    return addHeapObject(ret);
}, arguments); }
export function __wbg_then_16d107c451e9905d(arg0, arg1, arg2) {
    const ret = getObject(arg0).then(getObject(arg1), getObject(arg2));
    return addHeapObject(ret);
}
export function __wbg_then_6ec10ae38b3e92f7(arg0, arg1) {
    const ret = getObject(arg0).then(getObject(arg1));
    return addHeapObject(ret);
}
export function __wbg_versions_276b2795b1c6a219(arg0) {
    const ret = getObject(arg0).versions;
    return addHeapObject(ret);
}
export function __wbg_warn_b1370d804fa3e259(arg0) {
    console.warn(getObject(arg0));
}
export function __wbg_webkitGetAsEntry_7708af9f8b9981eb() { return handleError(function (arg0) {
    const ret = getObject(arg0).webkitGetAsEntry();
    return isLikeNone(ret) ? 0 : addHeapObject(ret);
}, arguments); }
export function __wbindgen_cast_0000000000000001(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Externref], shim_idx: 48, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, __wasm_bindgen_func_elem_594);
    return addHeapObject(ret);
}
export function __wbindgen_cast_0000000000000002(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Externref], shim_idx: 695, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, __wasm_bindgen_func_elem_4372);
    return addHeapObject(ret);
}
export function __wbindgen_cast_0000000000000003(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [NamedExternref("DragEvent")], shim_idx: 48, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, __wasm_bindgen_func_elem_594_2);
    return addHeapObject(ret);
}
export function __wbindgen_cast_0000000000000004(arg0, arg1) {
    // Cast intrinsic for `Ref(Slice(U8)) -> NamedExternref("Uint8Array")`.
    const ret = getArrayU8FromWasm0(arg0, arg1);
    return addHeapObject(ret);
}
export function __wbindgen_cast_0000000000000005(arg0, arg1) {
    // Cast intrinsic for `Ref(String) -> Externref`.
    const ret = getStringFromWasm0(arg0, arg1);
    return addHeapObject(ret);
}
export function __wbindgen_object_clone_ref(arg0) {
    const ret = getObject(arg0);
    return addHeapObject(ret);
}
export function __wbindgen_object_drop_ref(arg0) {
    takeObject(arg0);
}
function __wasm_bindgen_func_elem_594(arg0, arg1, arg2) {
    wasm.__wasm_bindgen_func_elem_594(arg0, arg1, addHeapObject(arg2));
}

function __wasm_bindgen_func_elem_594_2(arg0, arg1, arg2) {
    wasm.__wasm_bindgen_func_elem_594_2(arg0, arg1, addHeapObject(arg2));
}

function __wasm_bindgen_func_elem_4372(arg0, arg1, arg2) {
    try {
        const retptr = wasm.__wbindgen_add_to_stack_pointer(-16);
        wasm.__wasm_bindgen_func_elem_4372(retptr, arg0, arg1, addHeapObject(arg2));
        var r0 = getDataViewMemory0().getInt32(retptr + 4 * 0, true);
        var r1 = getDataViewMemory0().getInt32(retptr + 4 * 1, true);
        if (r1) {
            throw takeObject(r0);
        }
    } finally {
        wasm.__wbindgen_add_to_stack_pointer(16);
    }
}

function __wasm_bindgen_func_elem_4389(arg0, arg1, arg2, arg3) {
    wasm.__wasm_bindgen_func_elem_4389(arg0, arg1, addHeapObject(arg2), addHeapObject(arg3));
}

const AddedObjectFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_addedobject_free(ptr, 1));
const AddedRobotFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_addedrobot_free(ptr, 1));
const DroppedFolderFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_droppedfolder_free(ptr, 1));
const InterfaceClothFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_interfacecloth_free(ptr, 1));
const InterfaceControllerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_interfacecontroller_free(ptr, 1));
const InterfaceFEMDeformableFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_interfacefemdeformable_free(ptr, 1));
const InterfaceFluid2DFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_interfacefluid2d_free(ptr, 1));
const InterfaceHybridFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_interfacehybrid_free(ptr, 1));
const InterfaceMPMDeformableFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_interfacempmdeformable_free(ptr, 1));
const InterfaceMassSpringFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_interfacemassspring_free(ptr, 1));
const InterfaceMassSpringDeformableFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_interfacemassspringdeformable_free(ptr, 1));
const InterfaceMechanismStateFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_interfacemechanismstate_free(ptr, 1));
const InterfaceSimulatorFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_interfacesimulator_free(ptr, 1));
const RobotPlanFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_robotplan_free(ptr, 1));

function addHeapObject(obj) {
    if (heap_next === heap.length) heap.push(heap.length + 1);
    const idx = heap_next;
    heap_next = heap[idx];

    heap[idx] = obj;
    return idx;
}

function _assertClass(instance, klass) {
    if (!(instance instanceof klass)) {
        throw new Error(`expected instance of ${klass.name}`);
    }
}

const CLOSURE_DTORS = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(state => wasm.__wbindgen_export5(state.a, state.b));

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function dropObject(idx) {
    if (idx < 1028) return;
    heap[idx] = heap_next;
    heap_next = idx;
}

function getArrayF32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayF64FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat64ArrayMemory0().subarray(ptr / 8, ptr / 8 + len);
}

function getArrayJsValueFromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    const mem = getDataViewMemory0();
    const result = [];
    for (let i = ptr; i < ptr + 4 * len; i += 4) {
        result.push(takeObject(mem.getUint32(i, true)));
    }
    return result;
}

function getArrayU32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

let cachedFloat32ArrayMemory0 = null;
function getFloat32ArrayMemory0() {
    if (cachedFloat32ArrayMemory0 === null || cachedFloat32ArrayMemory0.byteLength === 0) {
        cachedFloat32ArrayMemory0 = new Float32Array(wasm.memory.buffer);
    }
    return cachedFloat32ArrayMemory0;
}

let cachedFloat64ArrayMemory0 = null;
function getFloat64ArrayMemory0() {
    if (cachedFloat64ArrayMemory0 === null || cachedFloat64ArrayMemory0.byteLength === 0) {
        cachedFloat64ArrayMemory0 = new Float64Array(wasm.memory.buffer);
    }
    return cachedFloat64ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function getObject(idx) { return heap[idx]; }

function handleError(f, args) {
    try {
        return f.apply(this, args);
    } catch (e) {
        wasm.__wbindgen_export3(addHeapObject(e));
    }
}

let heap = new Array(1024).fill(undefined);
heap.push(undefined, null, true, false);

let heap_next = heap.length;

function isLikeNone(x) {
    return x === undefined || x === null;
}

function makeMutClosure(arg0, arg1, f) {
    const state = { a: arg0, b: arg1, cnt: 1 };
    const real = (...args) => {

        // First up with a closure we increment the internal reference
        // count. This ensures that the Rust closure environment won't
        // be deallocated while we're invoking it.
        state.cnt++;
        const a = state.a;
        state.a = 0;
        try {
            return f(a, state.b, ...args);
        } finally {
            state.a = a;
            real._wbg_cb_unref();
        }
    };
    real._wbg_cb_unref = () => {
        if (--state.cnt === 0) {
            wasm.__wbindgen_export5(state.a, state.b);
            state.a = 0;
            CLOSURE_DTORS.unregister(state);
        }
    };
    CLOSURE_DTORS.register(real, state, state);
    return real;
}

function passArray32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getUint32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF64ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 8, 8) >>> 0;
    getFloat64ArrayMemory0().set(arg, ptr / 8);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeObject(idx) {
    const ret = getObject(idx);
    dropObject(idx);
    return ret;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;


let wasm;
export function __wbg_set_wasm(val) {
    wasm = val;
}
